import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import MapGL, { Source, Layer, Marker } from "react-map-gl";
import {
  Button,
  Empty,
  Form,
  Input,
  List,
  Modal,
  notification,
  Popconfirm,
  Radio,
  Select,
  Spin,
  Switch,
  Table,
  Tabs,
  Tooltip,
} from "antd";
import { ClearOutlined, DeleteOutlined } from "@ant-design/icons";
import * as htmlToImage from "html-to-image";
import { download } from "@mapbox/shp-write";
import { FaFileDownload } from "react-icons/fa";
import {
  LuListRestart,
  LuLogIn,
  LuMap,
  LuPrinter,
  LuSave,
} from "react-icons/lu";
import {
  IoFilter,
  IoGrid,
  IoListOutline,
  IoPrintOutline,
} from "react-icons/io5";
import polylabel from "polylabel";
import { geoArea } from "d3-geo";
import bbox from "@turf/bbox";
import center from "@turf/center";
import axios from "axios";
import { FaAngleDoubleRight } from "react-icons/fa";
import secureLocalStorage from "react-secure-storage";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import hostURL from "../../Common/constants";
import PracticeCard from "../PracticeList/Components/PracticeCard";
import WizardForm from "../ExplorebyGoals/wizardForm";
import {
  openWizard,
  nextWizardStep,
  prevWizardStep,
  setWizardStep,
  closeWizard,
} from "../../Redux/wizardSlice";
import {
  stateFilter,
  stateCode,
  safeLabels,
  landCoverTypeLabels,
  priorityLabels,
  safePractices,
  withinFloodplainPractices,
  outsideFloodplainPractices,
  waterBufferPractices,
  crepColorPool,
  crepColorMap as CREP_COLOR_EXPORT,
  crepCodes,
  pastureRateStates,
  practiceNoteData,
} from "./utils/helperFunctions";
import { setSelectedFips } from "../../Redux/filterSlice";
import { calculatePracticeScore, getMatchLabel } from "./utils/scoring";
import "./style.css";
import useTutorialSteps from "../Tutorial/useTutorialSteps";
import { setCurrentStep } from "../../Redux/tutorialSlice";
import {
  TbHexagonLetterP,
  TbHexagonLetterR,
  TbHexagonLetterS,
  TbHexagonLetterW,
} from "react-icons/tb";
import { resetFilterState } from "../../utils/resetFilterState";
import ResponsiveConfirm from "../../Components/ResponsiveConfirm";

const FieldReport = () => {
  // ***************** START NEW FILTERING STATE VARAIBLES **************************

  const allPractices = useSelector((state) => state.practice.allPractices);

  // Normalize CREP colors to a stable Map, even if something passed it as an object.
  const crepColorsRef = useRef(null);
  if (!crepColorsRef.current) {
    const maybe = CREP_COLOR_EXPORT;
    crepColorsRef.current =
      maybe instanceof Map ? maybe : new Map(Object.entries(maybe || {}));
  }
  const crepColors = crepColorsRef.current;

  const hasCrepColor = (k) =>
    crepColors instanceof Map
      ? crepColors.has(k)
      : Object.prototype.hasOwnProperty.call(crepColors, k);

  const getCrepColor = (k) =>
    crepColors instanceof Map ? crepColors.get(k) : crepColors?.[k];

  const setCrepColor = (k, v) => {
    if (crepColors instanceof Map) crepColors.set(k, v);
    else crepColors[k] = v;
    return v;
  };

  // ***************** END NEW FILTERING STATE VARAIBLES **************************
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [printList, setPrintList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soilData, setSoilData] = useState(null);
  const [safeData, setSafeData] = useState(null);
  const [floodplainData, setFloodplainData] = useState(null);
  const [waterData, setWaterData] = useState(null);
  const [hydroData, setHydroData] = useState(null);
  const [majorSoilTypes, setMajorSoilTypes] = useState([]);
  const [majorSoilArea, setMajorSoilArea] = useState([]);
  const [fieldBasics, setFieldBasics] = useState(null);
  const [fieldFeatureData, setFieldFeatureData] = useState(null);
  const [fieldAddress, setFieldAddress] = useState("Not Specified");
  const [fieldSize, setFieldSize] = useState(null);
  const [fieldRentalRate, setFieldRentalRate] = useState(null);
  const [soilTableData, setSoilTableData] = useState(null);
  const [grasslandData, setGrasslandData] = useState(null);
  const [pastureData, setPastureData] = useState(null);
  const [baseLayer, setBaseLayer] = useState(
    "mapbox://styles/mapbox/satellite-streets-v12"
  );
  const [layerSelectOpen, setLayerSelectOpen] = useState(false);
  const [soilLayerOn, setSoilLayerOn] = useState(true);
  const [safeLayerOn, setSafeLayerOn] = useState(false);
  const [floodplainLayerOn, setFloodplainLayerOn] = useState(false);
  const [waterLayerOn, setWaterLayerOn] = useState(false);
  const [filter, setFilter] = useState([]);
  const [highlightFeatureId, setHighlightFeatureId] = useState(null);
  const [isSavePopOpen, setIsSavePopOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [rentalRateType, setRentalRateType] = useState("soil");
  const [serviceCenterList, setServiceCenterList] = useState([]);
  const [preferredPractice, setPreferredPractice] = useState(null);
  const [userFavoritePractices, setUserFavoritePractices] = useState([]);
  const [userFavoritesLoaded, setUserFavoritesLoaded] = useState(false);
  const [practicesReady, setPracticesReady] = useState(false);
  const [preferredSignups, setPreferredSignups] = useState({
    G: true,
    C: true,
  });
  const [preferredIncentives, setPreferredIncentives] = useState({
    S: 0,
    R: 0,
    P: 0,
    W: 0,
  });
  const INCENTIVE_LABELS = {
    S: { label: "Signup incentive", note: "(1 time payment increase)" },
    R: { label: "Rental Rate incentive", note: "(Annual payment increase)" },
    P: { label: "Practice incentive", note: "(1 time payment increase)" },
    W: { label: "Water Quality Bonus", note: "(Through CLEAR30)" },
  };

  const ICONS = {
    S: <TbHexagonLetterS size="2em" color="maroon" />,
    R: <TbHexagonLetterR size="2em" color="maroon" />,
    P: <TbHexagonLetterP size="2em" color="maroon" />,
    W: <TbHexagonLetterW size="2em" color="maroon" />,
  };

  const [crepOption, setCrepOption] = useState("NO CREP");
  const isWizardOpen = useSelector((state) => state.wizard.open);
  const wizardStep = useSelector((state) => state.wizard.wizardStep);
  const practiceLoading = useSelector(
    (state) => state.practice.practiceLoading
  );
  const { handleNextStep } = useTutorialSteps();
  const { currentStep, isTutorialActive } = useSelector(
    (state) => state.tutorial
  );

  const [selectedSignUps, setSelectedSignUps] = useState(["G", "C", "Grslnd"]);

  const signupsList = [
    { label: "General", value: "G" },
    { label: "Continuous", value: "C" },
    { label: "Grassland", value: "Grslnd" },
  ];
  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef();
  const dispatch = useDispatch();
  // const printPracticeArray = [];
  const [mapLoaded, setMapLoaded] = useState(false);

  let nextColorIndex = 0;

  // const addPrintPracticeArray = (practice) => {
  //   if (!!practice && !printPracticeArray.includes(practice)) {
  //     printPracticeArray.push(practice);
  //   }
  // };

  // const removePrintPracticeArray = (practice) => {
  //   if (!!practice && printPracticeArray.includes(practice)) {
  //     const index = printPracticeArray.indexOf(practice);
  //     if (index > -1) {
  //       printPracticeArray.splice(index, 1);
  //     }
  //   }
  // };

  const handlePrint = () => {
    console.log(scoredAndSortedPractices);

    localStorage.setItem(
      "fieldreport:visiblePractices",
      JSON.stringify(scoredAndSortedPractices ?? [])
    );

    window.open("/FieldReport/Print", "_blank", "noopener,noreferrer");
  };

  const handleDownload = () => {
    const options = {
      filename: "Field Shapefile",
      outputType: "blob",
      compression: "DEFLATE",
      types: {
        polygon: "field",
      },
    };

    if (fieldFeatureData) {
      download(
        {
          type: "FeatureCollection",
          features: [fieldFeatureData],
        },
        options
      );
    }
  };

  const [form] = Form.useForm();
  const authToken = secureLocalStorage.getItem("token");
  const fieldDetails = secureLocalStorage.getItem("field-details");
  const {
    landCoverSelections,
    prioritySelections,
    rankingScore,
    selectedFips,
    selectedState,
    selectedCounty,
    disableInteractions,
  } = useSelector((state) => state.filter);

  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, []);

  useEffect(() => {
    const polygonFips = fieldBasics?.fips;

    if (!polygonFips) return;

    if (selectedFips && String(polygonFips) !== String(selectedFips)) {
      console.warn("FIPS mismatch — redirecting to draw polygon step");
      navigate("/Map", { replace: true });
    }
  }, [fieldBasics, selectedFips]);

  useEffect(() => {
    dispatch(closeWizard());
  }, []);

  const showPopconfirm = () => {
    setIsSavePopOpen(true);
  };

  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (type, title, message) => {
    api[type]({
      message: title,
      description: message,
    });
  };

  const saveSearchPopClose = () => {
    form.resetFields();
    setIsSavePopOpen(false);
  };

  const handleSignupChange = (value) => {
    setSelectedSignUps(value);
  };

  const storeSearchData = async (values, sroute) => {
    form.resetFields();
    setIsSavePopOpen(false);
    let imageDataURL = null;
    setLoading(true);

    await htmlToImage
      .toPng(document.getElementById("fieldReportMap"), { allowTaint: true })
      .then(function (dataurl) {
        imageDataURL = dataurl;
      });

    const searchedData = {
      selectedState: fieldBasics.state,
      selectedCounty: fieldBasics.county,
      landCoverSelections: landCoverSelections,
      prioritySelections: prioritySelections,
      rankingScore: rankingScore,
      mapimage: imageDataURL,
      data: fieldDetails,
    };

    const formData = {
      userId: secureLocalStorage.getItem("userRef"),
      route: window.location.href,
      formData: JSON.stringify(searchedData),
      mySearchName: values.SaveKey,
      stype: "checkEligibility",
    };

    setLoading(true);
    fetch(hostURL + "api/usersearchdata", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: authToken,
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((json) => {
        openNotificationWithIcon(
          "success",
          "Success",
          "Saved Your Search Criteria"
        );
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        openNotificationWithIcon("error", "Error", "Try Again Later");
      });
  };

  const getFieldAddress = async () => {
    const fieldGeometry = JSON.parse(fieldDetails.countyRate.rows[0].geometry);
    const centerPoint = center(fieldGeometry);
    const [lng, lat] = centerPoint.geometry.coordinates;
    setLoading(true);
    const response = await axios.get(
      "https://api.mapbox.com/search/geocode/v6/reverse?longitude=" +
        lng +
        "&latitude=" +
        lat +
        "&access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
    );
    if (response.data) {
      setTimeout(() => {
        setLoading(false);
      }, 6000);
      setFieldAddress(response.data.features[0].properties.full_address);
    }
  };

  const getFieldRentalRate = () => {
    const fieldRate = fieldDetails.fieldRate;
    const fieldSpecifics = fieldRate.rows;
    // Remove water from soil types
    const soilTypes = [
      ...new Set(fieldSpecifics.map((field) => field.musym)),
    ].filter((soilType) => soilType !== "W");

    const SoilTypeData = soilTypes.map((soilType) => {
      const fieldsBySoilType = fieldSpecifics?.filter(
        (field) => field.musym === soilType
      );
      const srr = fieldsBySoilType[0].srr;
      const areas = fieldsBySoilType.map((field) => field.area);
      const getTotalArea = (total, area) => {
        return total + area;
      };
      const totalArea = areas.reduce(getTotalArea, 0);
      return { type: soilType, srr: srr, totalArea: totalArea };
    });

    const getTopThree = (arr) => {
      arr.sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
      return arr.slice(0, 3);
    };

    const totalAreas = SoilTypeData.map((soilType) => soilType.totalArea);
    const topThreeTotalAreas = getTopThree(totalAreas);
    const topThreeSoilTypes = SoilTypeData?.filter((soilType) =>
      topThreeTotalAreas.includes(soilType.totalArea)
    );
    setMajorSoilTypes(topThreeSoilTypes);
    setMajorSoilArea(topThreeTotalAreas.reduce((a, b) => a + b, 0) * 0.000247105);
  };

  const getPracticeList = (queryState, queryCounty) => {
    setLoading(true);
    fetch(`${hostURL}api/statepractices/${queryState}/${queryCounty}`)
      .then((response) => response.json())
      .then((json) => {
        const respData = json[0];
        dispatch(setSelectedFips(String(respData.fips).padStart(5, "0")));

        // let toShowData =
        //   respData &&
        //   JSON.parse(
        //     respData.assetdata.substring(1, respData.assetdata.length - 1)
        //   );
        // setFilteredPractices(toShowData);
        // setAllPractices(toShowData);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  };

  // Add this once, near other useMemos/useStates
  const EMPTY_FC = useMemo(
    () => ({ type: "FeatureCollection", features: [] }),
    []
  );

  const interactiveLayerIds = useMemo(() => ["soil-layer"], []);

  // ****************** NEW FILTERING LOGIC STARTS HERE *************************

  const filteredByPriorityAndLandCover = useMemo(() => {
    return allPractices.filter((practice) => {
      if (
        !Array.isArray(practice.priorities) ||
        !Array.isArray(practice.landcover)
      )
        return false;

      const matchesPriority =
        prioritySelections.length === 0 ||
        prioritySelections.some((selected) =>
          practice.priorities?.some((p) => p.name === selected)
        );

      const matchesLandCover =
        landCoverSelections.length === 0 ||
        landCoverSelections.some((selected) =>
          practice.landcover?.includes(selected)
        );

      return matchesPriority && matchesLandCover;
    });
  }, [allPractices, prioritySelections, landCoverSelections]);

  // console.log(
  //   "✅ Step 1: matched land cover + priority",
  //   filteredByPriorityAndLandCover.map((p) => p.title)
  // );

  const waterBufferFilteredPractices = useMemo(() => {
    const fieldHasWaterBuffer = waterData?.features?.length > 0;

    return filteredByPriorityAndLandCover.filter((practice) => {
      const practiceCode = practice.practiceNumber;
      const requiresWaterBuffer =
        waterBufferPractices.hasOwnProperty(practiceCode);

      if (requiresWaterBuffer && !fieldHasWaterBuffer) return false;
      return true;
    });
  }, [filteredByPriorityAndLandCover, waterData, waterBufferPractices]);

  // console.log(
  //   "✅ Step 2: after water buffer",
  //   waterBufferFilteredPractices.map((p) => p.title)
  // );

  const floodplainFilteredPractices = useMemo(() => {
    const fieldHasFloodplain = floodplainData?.features?.length > 0;

    return waterBufferFilteredPractices.filter((practice) => {
      const practiceCode = practice.practiceNumber;
      const mustBeInFloodplain =
        withinFloodplainPractices.includes(practiceCode);
      const mustBeOutsideFloodplain =
        outsideFloodplainPractices.includes(practiceCode);

      if (mustBeInFloodplain && !fieldHasFloodplain) return false;
      if (mustBeOutsideFloodplain && fieldHasFloodplain) return false;

      return true;
    });
  }, [
    waterBufferFilteredPractices,
    floodplainData,
    withinFloodplainPractices,
    outsideFloodplainPractices,
  ]);

  // console.log(
  //   "✅ Step 3: after floodplain",
  //   floodplainFilteredPractices.map((p) => p.title)
  // );

  const safeFilteredPractices = floodplainFilteredPractices.filter(
    (practice) => {
      const fieldHasSafeZone = safeData?.features?.length > 0;
      const safePracticeTitles = Object.values(safePractices); // or adjust if already an array
      const isSafePractice = safePracticeTitles.includes(practice.title);

      if (isSafePractice && !fieldHasSafeZone) {
        return false; // exclude SAFE practices if field not in SAFE zone
      }

      return true; // include all else
    }
  );

  // console.log(
  //   "✅ Step 4: after SAFE",
  //   safeFilteredPractices.map((p) => p.title)
  // );

  const hydroFilteredPractices = safeFilteredPractices.filter((practice) => {
    const fieldTotalArea = hydroData?.features
      ?.map((feature) => feature.properties.area)
      .reduce((a, b) => a + b, 0);
    const fieldHydricArea = hydroData?.features
      ?.map((feature) => {
        if (feature.properties.gridcode > 50) {
          return feature.properties.area;
        } else {
          return 0;
        }
      })
      .reduce((a, b) => a + b, 0);
    const fieldHasHydricSoil =
      fieldHydricArea / fieldTotalArea > 0.5 ? true : false;

    if (practice.practiceNumber === "CP23" && !fieldHasHydricSoil) {
      return false;
    } else {
      return true;
    }
  });

  // console.log(
  //   "✅ Step 5: after hydric soil",
  //   hydroFilteredPractices.map((p) => p.title)
  // );

  const scoredAndSortedPractices = useMemo(() => {
    let filteredVisiblePractices = [];
    let generalVisiblePractices = [];
    let continuousVisiblePractices = [];
    let grasslandVisiblePractices = [];

    if (selectedSignUps.length) {
      if (selectedSignUps.includes("G")) {
        generalVisiblePractices = [
          ...hydroFilteredPractices.filter((x) => x.general),
        ];
      }
      if (selectedSignUps.includes("C")) {
        continuousVisiblePractices = [
          ...hydroFilteredPractices.filter(
            (x) => x.continuous && x.continuous !== "false"
          ),
        ];
      }
      if (selectedSignUps.includes("Grslnd")) {
        grasslandVisiblePractices = [
          ...hydroFilteredPractices.filter((x) => x.title.includes("88")),
        ];
      }
      filteredVisiblePractices = [
        ...generalVisiblePractices,
        ...continuousVisiblePractices,
        ...grasslandVisiblePractices,
      ];
      filteredVisiblePractices = [
        ...new Map(
          filteredVisiblePractices.map((item) => [item.title, item])
        ).values(),
      ];
    }
    return filteredVisiblePractices
      .map((practice) => {
        const score = calculatePracticeScore(practice, rankingScore);
        const { scoreLabel, scoreClassName } = getMatchLabel(
          score,
          prioritySelections.length
        );

        return {
          ...practice,
          score,
          scoreLabel,
          scoreClassName,
        };
      })
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        const getCPNum = (p) => {
          const match = p.practiceNumber?.match(/\d+/);
          return match ? parseInt(match[0], 10) : 0;
        };

        return getCPNum(a) - getCPNum(b);
      });
  }, [hydroFilteredPractices, rankingScore, prioritySelections]);

  // ****************** NEW FILTERING LOGIC ENDS HERE *************************

  /******************* START TUTORIAL FUNCTIONS & EFFECTS ************/

  useEffect(() => {
    setPageIndex(1);
  }, [prioritySelections, landCoverSelections]);

  useEffect(() => {
    if (isTutorialActive && currentStep === 61) setLayerSelectOpen(true);
    if (isTutorialActive && currentStep === 62) setLayerSelectOpen(false);
  }, [currentStep]);

  const pageSize = useMemo(
    () => (landCoverSelections.includes("pasture") ? 8 : 7),
    [landCoverSelections]
  );

  // Map the county’s available CREP codes once
  const availableCrepCodes = useMemo(() => {
    return new Set(
      fieldDetails?.crepArea?.rows?.map((r) => String(r.code)) ?? []
    );
  }, [fieldDetails]);

  // Normalize CREP name -> county code (handles undefined + whitespace)
  const getCrepCode = useCallback((name) => {
    const code = crepCodes?.[String(name ?? "").trim()];
    return code == null ? "" : String(code);
  }, []);

  // Single source of truth: eligible CREPs for a given practice at THIS field
  const getEligibleCREPs = useCallback(
    (practice) => {
      if (!Array.isArray(practice?.crepInfo)) return [];
      return practice.crepInfo.filter((c) =>
        availableCrepCodes.has(getCrepCode(c.crepname))
      );
    },
    [availableCrepCodes, getCrepCode]
  );

  const firstCREPIndex = useMemo(() => {
    if (!Array.isArray(scoredAndSortedPractices)) return -1;
    return scoredAndSortedPractices.findIndex(
      (p) => getEligibleCREPs(p).length > 0
    );
  }, [scoredAndSortedPractices, getEligibleCREPs]);

  const targetCREPPage =
    firstCREPIndex >= 0 ? Math.floor(firstCREPIndex / pageSize) + 1 : -1;

  const hasEligibleCREP = firstCREPIndex >= 0;

  const CREP_START = 63;
  const CREP_END = 65;
  const CREP_BEFORE = 62; // where to land when going backward
  const CREP_AFTER = 66; // where to land when going forward

  const inCrepBlock = (step) => step >= CREP_START && step <= CREP_END;

  const prevStepRef = useRef(currentStep);
  const jumpingRef = useRef(false); // prevents re-entrancy while we jump

  useEffect(() => {
    if (!isTutorialActive || hasEligibleCREP) {
      prevStepRef.current = currentStep;
      jumpingRef.current = false;
      return;
    }

    // If not in the CREP block, just track & exit.
    if (!inCrepBlock(currentStep)) {
      prevStepRef.current = currentStep;
      jumpingRef.current = false;
      return;
    }

    // Avoid loops while dispatching a jump
    if (jumpingRef.current) return;

    const prev = prevStepRef.current;
    const goingForward = currentStep > prev;
    const target = goingForward ? CREP_AFTER : CREP_BEFORE;

    if (currentStep !== target) {
      jumpingRef.current = true;
      dispatch(setCurrentStep(target));
      // Don't update prevStepRef yet; wait for Redux to update currentStep.
      return;
    }

    jumpingRef.current = false;
    prevStepRef.current = currentStep;
  }, [isTutorialActive, hasEligibleCREP, currentStep, dispatch]);

  useEffect(() => {
    if (!isTutorialActive || currentStep !== 63 || firstCREPIndex < 0) return;

    // Go to the page that contains the first eligible CREP card
    if (pageIndex !== targetCREPPage) {
      setPageIndex(targetCREPPage);
      return; // wait for pagination to update
    }

    const target = scoredAndSortedPractices[firstCREPIndex];
    if (!target) return;

    // Preselect the first eligible CREP radio (so the tutorial step sees it)
    const eligible = getEligibleCREPs(target);
    if (eligible[0]) {
      setCrepOption(eligible[0].crepname);
    }
  }, [
    isTutorialActive,
    currentStep,
    firstCREPIndex,
    targetCREPPage,
    pageIndex,
    scoredAndSortedPractices,
    getEligibleCREPs,
  ]);

  /**
   * Steps 56 (closed) & 57 (open) — use the top-ranked practice.
   */
  useEffect(() => {
    if (!isTutorialActive || !practicesReady) return;

    // Always land on the page that has the target card for these steps

    if (currentStep === 56 || currentStep === 59) {
      // force closed & clear CREP selection
      setPreferredPractice(null);
      setCrepOption("NO CREP");
    }

    if (currentStep === 57 || currentStep === 58) {
      const target = scoredAndSortedPractices[0];
      if (!target) return;
      const title = target.title?.split("-")[0]?.trim();
      // open the card, but keep NO CREP
      if (preferredPractice !== title) setPreferredPractice(title);
      if (crepOption !== "NO CREP") setCrepOption("NO CREP");
    }
  }, [
    isTutorialActive,
    practicesReady,
    currentStep,
    scoredAndSortedPractices,
    pageIndex,
    preferredPractice,
    crepOption,
  ]);

  /**
   * Steps 63 (closed), 64 & 65 (open )
   */
  useEffect(() => {
    if (!isTutorialActive || !practicesReady || firstCREPIndex < 0) return;

    // For 63–65 we always jump to the page with the first CREP-eligible card
    if (
      (currentStep === 63 || currentStep === 64 || currentStep === 65) &&
      targetCREPPage > 0 &&
      pageIndex !== targetCREPPage
    ) {
      setPageIndex(targetCREPPage);
      return; // wait for pagination before touching card state
    }

    const target = scoredAndSortedPractices[firstCREPIndex];
    const title = target?.title?.split("-")[0]?.trim();

    if (currentStep === 63) {
      // ensure closed and CREP not selected
      setPreferredPractice(null);
      setCrepOption("NO CREP");
      return;
    }

    if (currentStep === 64 || currentStep === 65) {
      // open target card
      if (title && preferredPractice !== title) setPreferredPractice(title);
    }
  }, [
    isTutorialActive,
    practicesReady,
    currentStep,
    firstCREPIndex,
    targetCREPPage,
    pageIndex,
    scoredAndSortedPractices,
    getEligibleCREPs,
    preferredPractice,
    crepOption,
  ]);

  /******************* END TUTORIAL FUNCTIONS & EFFECTS ************/

  const renderCountyRateTable = ({
    title,
    description,
    dataKey,
    county,
    ratePerAcre,
    acreage,
    printList,
  }) => {
    const columns = [
      { title: "County", dataIndex: "county", key: "county", align: "center" },
      {
        title: "Rental Rate",
        dataIndex: "rental_rate",
        key: "rental_rate",
        align: "center",
      },
      {
        title: "Acreage",
        dataIndex: "acreage",
        key: "acreage",
        align: "center",
      },
    ];

    return (
      <>
        <h3
          className={
            printList ? "report-section-header print" : "report-section-header"
          }
        >
          {title}
          <sup>[1]</sup>:
        </h3>

        <p className="report-table-information">{description}</p>

        <Table
          className={printList ? "print report-table" : "report-table"}
          columns={columns}
          dataSource={[
            {
              key: dataKey || title,
              county,
              rental_rate: ratePerAcre ? `$${ratePerAcre}/acre` : "N/A",
              acreage: Number.isFinite(Number(acreage))
                ? Math.round(Number(acreage) * 100) / 100
                : "N/A",
            },
          ]}
          pagination={false}
          size="large"
        />
      </>
    );
  };

  const getUserFavorites = () => {
    fetch(
      `${hostURL}api/userfavourite/${secureLocalStorage.getItem(
        "userRef"
      )}/${selectedState}/${selectedCounty}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: authToken,
        },
      }
    )
      .then((response) => response.json())
      .then((json) => {
        const rawFavorites = Array.isArray(json)
          ? json.map((item) => item.favTitle)
          : json.data && Array.isArray(json.data)
          ? json.data.map((item) => item.favTitle)
          : [];

        const respData = Array.from(new Set(rawFavorites));
        setUserFavoritePractices(respData);
        setUserFavoritesLoaded(true); // ✅ mark as ready
      })
      .catch((error) => {
        console.error("❌ Error fetching favorites:", error);
        setUserFavoritesLoaded(true); // still mark as ready to unblock UI
      });
  };

  const getPayEstimateText = (
    getEstimatedPayment,
    getPasturePayment,
    practice,
    practiceTitle
  ) => {
    if (practiceTitle === "CP88") {
      return (
        <div
          className="report-practice-list-short"
          style={{
            border: "2px solid gray",
          }}
        >
          <h3>
            Your Estimated Field Rental Rate is $
            {Math.round(fieldSize * grasslandData?.grr).toLocaleString()}{" "}
            (Grassland Signup)
            <sup>[1]</sup>
          </h3>
          <p>
            Your total rental rate is estimated to be:
            <b>
              {" "}
              ${Math.round(fieldSize * grasslandData?.grr).toLocaleString()}
            </b>
            <sup>[1]</sup>. Your total rental rate per acre is estimated to be:
            <b>
              {" "}
              ${grasslandData?.grr.toLocaleString()}
              /acre
            </b>
            <sup>[1]</sup>.
          </p>
          <p>
            By signing up for <b>CP88</b>, you are not eligible for any
            incentives. This results in an estimated potential first-year
            payment of
            <b>
              {" "}
              ${Math.round(
                fieldSize * grasslandData?.grr
              ).toLocaleString()}{" "}
            </b>
            for your field.
          </p>
          <p className="detail-right field-report">
            <p className="detail-button">
              <a
                href={`/Practice/${selectedFips}/${btoa(
                  practice.pdfLink
                )}/${btoa(practice.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "maroon",
                  fontWeight: "bold",
                }}
              >
                Details <FaAngleDoubleRight /> <br />
              </a>
            </p>
          </p>
        </div>
      );
    } else if (
      (practiceTitle === "CP22" &&
        landCoverSelections.length === 1 &&
        landCoverSelections[0] === "pasture") ||
      practiceTitle === "CP29" ||
      practiceTitle === "CP30"
    ) {
      return (
        <div
          className="report-practice-list-short report-practice-detail"
          style={{
            border: "2px solid gray",
          }}
        >
          <h3>
            Your Estimated Field Rental Rate is{" "}
            {preferredSignups.G && (
              <span>
                ${getPasturePayment(0.85).toLocaleString()} (General Signup)
                <sup>[1]</sup>
              </span>
            )}
            {preferredSignups.G && preferredSignups.C ? " or " : ""}
            {preferredSignups.C && (
              <span>
                ${getPasturePayment(0.9).toLocaleString()} (Continuous Signup)
                <sup>[1]</sup>
              </span>
            )}
          </h3>
          <div>
            <p>
              Your total marginal pastureland rental rate is estimated to be:
              <b>
                {" "}
                ${Math.round(fieldSize * pastureData?.mplrr).toLocaleString()}
              </b>
              <sup>[1]</sup>. Your total marginal pastureland rental rate per
              acre is estimated to be:
              <b>
                {" "}
                ${Math.round(pastureData?.mplrr).toLocaleString()}
                /acre
              </b>
              <sup>[1]</sup>. As determined by USDA, landowners will receive 85%
              for general signup and 90% for continuous signup of the estimated
              rental rate.
            </p>
            <p>
              By signing up for <b>{preferredPractice}</b>,
              {(preferredIncentives.S ||
                preferredIncentives.R ||
                preferredIncentives.P) && <span> you are eligible for </span>}
              {preferredIncentives.S && <span>a 32.5% signup incentive, </span>}
              {preferredIncentives.R && (
                <span>a 20% rental rate incentive, </span>
              )}
              {preferredIncentives.P && <span>a 50% practice incentive, </span>}
              and <b>{preferredPractice}</b> supports{" "}
              {!preferredSignups.C ? (
                <>
                  general signup, which has a maximum payment cap set at
                  $240/acre. This results in an estimated potential first-year
                  payment of <b>${getPasturePayment(0.85).toLocaleString()}</b>{" "}
                  for your field.
                </>
              ) : !preferredSignups.G ? (
                <>
                  continuous signup, which has a maximum payment cap set at
                  $300/acre. This results in an estimated potential first-year
                  payment of <b>${getPasturePayment(0.9).toLocaleString()}</b>{" "}
                  for your field.
                </>
              ) : (
                "both general and continuous signup."
              )}
              {preferredSignups.C && preferredSignups.G && (
                <>
                  <span>
                    {" "}
                    For general signup, the cap is set at $240/acre, resulting
                    in an estimated potential first-year payment of{" "}
                    <b>${getPasturePayment(0.85).toLocaleString()}</b> for your
                    field.
                  </span>
                  <span>
                    {" "}
                    For continuous signup, the cap is set at $300/acre,
                    resulting in an estimated potential first-year payment of{" "}
                    <b>${getPasturePayment(0.9).toLocaleString()}</b>
                    <sup>[1]</sup> for your field.
                  </span>
                </>
              )}
            </p>
            <p>{practiceNoteData[preferredPractice]}</p>
          </div>
          {disableInteractions ? (
            <p className={"detail-right field-report"}>
              <p className="detail-button">
                <a
                  href={`/Practice/${selectedFips}/${btoa(
                    practice.pdfLink
                  )}/${btoa(practice.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "maroon",
                    fontWeight: "bold",
                  }}
                >
                  Details &raquo;
                </a>
              </p>
            </p>
          ) : (
            <p className={"detail-right field-report"}>
              <p className="detail-button">
                <a
                  href={`/Practice/${selectedFips}/${btoa(
                    practice.pdfLink
                  )}/${btoa(practice.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "maroon",
                    fontWeight: "bold",
                  }}
                >
                  Details <FaAngleDoubleRight />
                </a>
              </p>
            </p>
          )}
        </div>
      );
    } else {
      return (
        <div
          className="report-practice-list-short .report-practice-detail"
          style={{
            border: "2px solid gray",
          }}
        >
          <h3>
            Your Estimated Field Rental Rate is{" "}
            {preferredSignups.G && (
              <span>
                ${getEstimatedPayment(0.85).toLocaleString()} (General Signup)
                <sup>[1]</sup>
              </span>
            )}
            {preferredSignups.G && preferredSignups.C ? " or " : ""}
            {preferredSignups.C && (
              <span>
                ${getEstimatedPayment(0.9).toLocaleString()} (Continuous Signup)
                <sup>[1]</sup>
              </span>
            )}
          </h3>
          <div>
            <p>
              The top soil types in your field are{" "}
              {majorSoilTypes &&
                majorSoilTypes.map((soilType, index) => (
                  <span key={soilType.type}>
                    {`${soilType.type} (${
                      Math.round(soilType.totalArea * 0.0002471 * 100) / 100
                    } acres @ $${soilType.srr}/acre)`}
                    {index < majorSoilTypes.length - 1 && ", "}
                  </span>
                ))}
              . To understand these soil type codes, please find the details{" "}
              <a
                href="https://www.nrcs.usda.gov/publications/Map%20Unit%20Description%20%28Brief%2C%20Generated%29%20Query%20by%20Soil%20Survey%20Area%20and%20Map%20Unit%20%28All%20Components%29.html"
                target="_blank"
                style={{ color: "maroon" }}
                rel="noreferrer"
              >
                here
              </a>
              .
            </p>
            <p>
              Your total weighted average rental rate is estimated to be:
              <b> ${fieldRentalRate.toLocaleString()}</b>
              <sup>[1]</sup>. Your total weighted average rental rate per acre
              is estimated to be:
              <b>
                {" "}
                ${Math.round(fieldRentalRate / fieldSize).toLocaleString()}
                /acre
              </b>
              <sup>[1]</sup>. As determined by USDA, landowners will receive 85%
              for general signup and 90% for continuous signup of the estimated
              rental rate.
            </p>
            <p>
              By signing up for <b>{preferredPractice}</b>,
              {(preferredIncentives.S ||
                preferredIncentives.R ||
                preferredIncentives.P) && <span> you are eligible for </span>}
              {preferredIncentives.S && <span>a 32.5% signup incentive, </span>}
              {preferredIncentives.R && (
                <span>a 20% rental rate incentive, </span>
              )}
              {preferredIncentives.P && <span>a 50% practice incentive, </span>}
              and <b>{preferredPractice}</b> supports{" "}
              {!preferredSignups.C ? (
                <>
                  general signup, which has a maximum payment cap set at
                  $240/acre. This results in an estimated potential first-year
                  payment of{" "}
                  <b>${getEstimatedPayment(0.85).toLocaleString()}</b> for your
                  field.
                </>
              ) : !preferredSignups.G ? (
                <>
                  continuous signup, which has a maximum payment cap set at
                  $300/acre. This results in an estimated potential first-year
                  payment of <b>${getEstimatedPayment(0.9).toLocaleString()}</b>{" "}
                  for your field.
                </>
              ) : (
                "both general and continuous signup."
              )}
              {preferredSignups.C && preferredSignups.G && (
                <>
                  <span>
                    {" "}
                    For general signup, the cap is set at $240/acre, resulting
                    in an estimated potential first-year payment of{" "}
                    <b>${getEstimatedPayment(0.85).toLocaleString()}</b> for
                    your field.
                  </span>
                  <span>
                    {" "}
                    For continuous signup, the cap is set at $300/acre,
                    resulting in an estimated potential first-year payment of{" "}
                    <b>${getEstimatedPayment(0.9).toLocaleString()}</b>
                    <sup>[1]</sup> for your field.
                  </span>
                </>
              )}
            </p>
            <p>{practiceNoteData[preferredPractice]}</p>
          </div>
          {disableInteractions ? (
            <p className={"detail-right field-report"}>
              <p className="detail-button">
                <a
                  href={`/Practice/${selectedFips}/${btoa(
                    practice.pdfLink
                  )}/${btoa(practice.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "maroon",
                    fontWeight: "bold",
                  }}
                >
                  Details &raquo;
                </a>
              </p>
            </p>
          ) : (
            <p className={"detail-right field-report"}>
              <p className="detail-button">
                <a
                  href={`/Practice/${selectedFips}/${btoa(
                    practice.pdfLink
                  )}/${btoa(practice.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "maroon",
                    fontWeight: "bold",
                  }}
                >
                  Details <FaAngleDoubleRight />
                </a>
              </p>
            </p>
          )}
        </div>
      );
    }
  };

  const getPayEstimateTextWithCREP = (
    option,
    getEstimatedPayment,
    getPasturePayment,
    practice,
    practiceTitle
  ) => {
    if (
      pastureRateStates.includes(selectedState) &&
      practiceTitle === "CP22" &&
      landCoverSelections.length === 1 &&
      landCoverSelections[0] === "pasture"
    ) {
      return (
        <div
          className="report-practice-list-short crep-payment"
          style={{
            border: `2px solid ${getCrepColor(option.crepname) || "#888888"}`,
          }}
        >
          <h3>
            <span>
              Your Estimated Field Rental Rate is $
              {Math.round(
                getPasturePayment(1) *
                  (parseInt(option.newenrollmentsrr) / 100) +
                  parseInt(option.stateincentive) * fieldSize
              ).toLocaleString()}
              ({option.crepname} Initiative)
              <sup>[1]</sup>
            </span>
          </h3>
          <div>
            <p>
              Your total marginal pastureland rental rate is estimated to be:
              <b>
                {" "}
                ${Math.round(fieldSize * pastureData?.mplrr).toLocaleString()}
              </b>
              <sup>[1]</sup>. Your total marginal pastureland rental rate per
              acre is estimated to be:
              <b>
                {" "}
                ${Math.round(pastureData?.mplrr).toLocaleString()}
                /acre
              </b>
              <sup>[1]</sup>. Enrollment through
              <b> {option.crepname} </b> includes additional incentives, such as
              an annual <b> {option.newenrollmentsrr}% </b>
              soil rental rate for new enrollments and annual
              <b>
                {" "}
                ${Number(option.stateincentive).toLocaleString()}
                /acre{" "}
              </b>
              state incentive. This results in an estimated potential first-year
              payment of
              <b>
                {" "}
                $
                {Math.round(
                  getPasturePayment(1) *
                    (parseInt(option.newenrollmentsrr) / 100) +
                    parseInt(option.stateincentive) * fieldSize
                ).toLocaleString()}
              </b>
              <sup>[1]</sup>
              for your field.
            </p>
            <p>
              Please contact your local FSA office for more details and official
              offers.
            </p>
          </div>

          <p className="detail-right field-report">
            <p className="detail-button">
              <a
                href={`/Practice/${selectedFips}/${btoa(
                  practice.pdfLink
                )}/${btoa(practice.title)}/${btoa(option.crepname)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "maroon",
                  fontWeight: "bold",
                }}
              >
                Details <FaAngleDoubleRight /> <br />
              </a>
            </p>
          </p>
        </div>
      );
    } else {
      return (
        <div
          className="report-practice-list-short crep-payment"
          style={{
            border: `2px solid ${getCrepColor(option.crepname) || "#888888"}`,
          }}
        >
          <h3>
            <span>
              Your Estimated Field Rental Rate is $
              {Math.round(
                getEstimatedPayment(1) *
                  (parseInt(option.newenrollmentsrr) / 100) +
                  parseInt(option.stateincentive) * fieldSize
              ).toLocaleString()}
              ({option.crepname} Initiative)
              <sup>[1]</sup>
            </span>
          </h3>
          <div>
            <p>
              The top soil types in your field are{" "}
              {majorSoilTypes &&
                majorSoilTypes.map((soilType, index) => (
                  <span key={soilType.type}>
                    {`${soilType.type} (${
                      Math.round(soilType.totalArea * 0.0002471 * 100) / 100
                    } acres @ $${soilType.srr}/acre)`}
                    {index < majorSoilTypes.length - 1 && ", "}
                  </span>
                ))}
              . To understand these soil type codes, please find the details{" "}
              <a
                href="https://www.nrcs.usda.gov/publications/Map%20Unit%20Description%20%28Brief%2C%20Generated%29%20Query%20by%20Soil%20Survey%20Area%20and%20Map%20Unit%20%28All%20Components%29.html"
                target="_blank"
                style={{
                  color: "maroon",
                }}
                rel="noreferrer"
              >
                here
              </a>
              .
            </p>
            <p>
              Your total weighted average rental rate is estimated to be:
              <b> ${fieldRentalRate.toLocaleString()}</b>
              <sup>[1]</sup>. Your total weighted average rental rate per acre
              is estimated to be:
              <b>
                {" "}
                ${Math.round(fieldRentalRate / fieldSize).toLocaleString()}
                /acre
              </b>
              <sup>[1]</sup>. Enrollment through
              <b> {option.crepname} </b> includes additional incentives, such as
              an annual <b> {option.newenrollmentsrr}% </b>
              soil rental rate for new enrollments and annual
              <b>
                {" "}
                ${Number(option.stateincentive).toLocaleString()}
                /acre{" "}
              </b>
              state incentive. This results in an estimated potential first-year
              payment of
              <b>
                {" "}
                $
                {Math.round(
                  getEstimatedPayment(1) *
                    (parseInt(option.newenrollmentsrr) / 100) +
                    parseInt(option.stateincentive) * fieldSize
                ).toLocaleString()}
              </b>
              <sup>[1]</sup>
              for your field.
            </p>
            <p>
              Please contact your local FSA office for more details and official
              offers.
            </p>
          </div>

          <p className="detail-right field-report">
            <p className="detail-button">
              <a
                href={`/Practice/${selectedFips}/${btoa(
                  practice.pdfLink
                )}/${btoa(practice.title)}/${btoa(option.crepname)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "maroon",
                  fontWeight: "bold",
                }}
              >
                Details <FaAngleDoubleRight /> <br />
              </a>
            </p>
          </p>
        </div>
      );
    }
  };

  function getTopThreeSoilsTable(fieldRateRows) {
    if (!Array.isArray(fieldRateRows)) return [];

    return Object.values(
      fieldRateRows.reduce((acc, r) => {
        const k = r.musym;
        acc[k] ??= { key: k, soil_type: k, srr: r.srr, totalArea: 0 };
        acc[k].totalArea += r.area;
        return acc;
      }, {})
    )
      .sort((a, b) => b.totalArea - a.totalArea)
      .slice(0, 3)
      .map((s) => {
        const rawSrr = s.srr;

        const isMissingSrr =
          rawSrr === null ||
          rawSrr === undefined ||
          rawSrr === "null" ||
          rawSrr === "";

        return {
          key: s.key,
          soil_type: s.soil_type,
          raw_srr: rawSrr, // keep the original value for logic checks
          rental_rate: isMissingSrr ? "N/A" : `$${rawSrr}/acre`,
          acreage: Math.round(s.totalArea * 0.000247105 * 100) / 100,
          isMissingSrr,
        };
      });
  }
  const topThreeSoilsTable = useMemo(() => {
    return getTopThreeSoilsTable(fieldDetails?.fieldRate?.rows);
  }, [fieldDetails]);

  const hasMissingTopThreeSrr = useMemo(() => {
    return topThreeSoilsTable?.some(
      (row) =>
        row?.isMissingSrr ||
        row?.raw_srr === null ||
        row?.raw_srr === undefined ||
        row?.raw_srr === "null"
    );
  }, [topThreeSoilsTable]);

  const favOnchange = (val, title) => {
    const pTitle = title.split("#")[0];
    const pLink = title.split("#")[1];
    if (val === 1) {
      const formData = {
        userId: secureLocalStorage.getItem("userRef"),
        favTitle: pTitle,
        favPdfLink: pLink,
        state: selectedState,
        county: selectedCounty,
      };

      setLoading(true);
      fetch(hostURL + "api/userfavourite", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: authToken,
        },
        body: JSON.stringify(formData),
      })
        .then((response) => response.json())
        .then((json) => {
          getUserFavorites();
          openNotificationWithIcon(
            "success",
            // "Success",
            "Added to Favorites"
          );
          setLoading(false);
        })
        .catch((error) => {
          openNotificationWithIcon("error", "Error", "Try Again Later");
        });
    } else if (val === 0) {
      const formData = {
        userId: secureLocalStorage.getItem("userRef"),
        favTitle: pTitle,
        state: selectedState,
        county: selectedCounty,
      };

      setLoading(true);
      fetch(
        `${hostURL}api/userfavourite/${formData.userId}/${formData.favTitle}/${formData.state}/${formData.county}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: authToken,
          },
          body: JSON.stringify(formData),
        }
      )
        .then((response) => response.json())
        .then((json) => {
          getUserFavorites();
          notification.success({
            description: "Removed from Favorites",
            icon: <DeleteOutlined style={{ color: "#faad14" }} />,
          });
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          openNotificationWithIcon("error", "Error", "Try Again Later");
        });
    }
  };

  const getServiceCenter = async (queryState, queryCounty) => {
    const response = await axios.post(hostURL + "api/field/service-center", {
      state: queryState,
      county: queryCounty,
    });
    if (response) {
      const fsaServiceCenters = response.data.serviceCenter.rows.filter(
        (item) => item.service_center.includes("Farm Service Agency")
      );
      setServiceCenterList(fsaServiceCenters);
    }
  };

  const handleLayerSelect = () => {
    if (isTutorialActive && currentStep === 60) handleNextStep();
    setLayerSelectOpen(!layerSelectOpen);
  };

  const getMarkerLocation = (geometry) => {
    let output = [];
    if (geometry.type === "Polygon") {
      output = polylabel(geometry.coordinates, 0.000001);
    } else {
      let maxArea = 0,
        maxPolygon = [];
      for (let i = 0, l = geometry.coordinates.length; i < l; i++) {
        const p = geometry.coordinates[i];
        const area = geoArea({ type: "Polygon", coordinates: p });
        if (area > maxArea) {
          maxPolygon = p;
          maxArea = area;
        }
      }
      output = polylabel(maxPolygon, 0.000001);
    }
    return output;
  };

  const onMouseMove = (e) => {
    const feature = e?.features?.[0];
    if (feature) {
      setFilter(["in", "gid", feature.id]);
      setHighlightFeatureId(feature.id);
    } else {
      setFilter(["in", "gid", ""]);
      setHighlightFeatureId(null);
    }
  };

  // useEffect(() => {
  //   if (fieldBasics) {
  //     setLoading(true);
  //     fetch(hostURL + "api/counties?state=" + stateCode[fieldBasics.state])
  //       .then((response) => response.json())
  //       .then((json) => {
  //         setCountyListData(json);
  //         setLoading(false);
  //       })
  //       .catch((error) => console.error(error));
  //   }
  // }, [fieldBasics]);

  // useEffect(() => {
  //   if (countyListData && fieldBasics) {
  //     let countyDataArray = countyListData?.filter(
  //       (item) => item.cname === fieldBasics.county
  //     );
  //     let assetData =
  //       countyDataArray.length > 0 ? countyDataArray[0].assetdata : null;
  //     let topPracticeList = assetData
  //       ? assetData.replace('"', "")?.split(",")
  //       : null;
  //     setselectedTopPracticeList(topPracticeList);
  //   }
  // }, [countyListData, fieldBasics]);

  useEffect(() => {
    if (fieldDetails) {
      const basics = fieldDetails.countyRate.rows[0];
      setFieldBasics(basics);
      getFieldRentalRate();
      setFieldSize(basics.area * 0.000247105);
      setFieldFeatureData({
        type: "Feature",
        geometry: JSON.parse(basics.geometry),
      });
      getPracticeList(stateCode[basics.state], basics.county);
      getServiceCenter(basics.state, basics.county);
      getFieldAddress();

      // Setup soil, floodplain, safe, and water buffer layers
      const soilTableData = fieldDetails.fieldRate.rows.map((soil, index) => {
        return {
          key: soil.gid,
          soil_type: soil.musym,
          rental_rate: "$" + soil.srr + "/acre",
          acreage: Math.round(soil.area * 0.000247105 * 100) / 100,
        };
      });
      setSoilTableData(soilTableData.sort((a, b) => b.acreage - a.acreage));

      const soilFeatures = fieldDetails.fieldRate.rows.map((soil) => {
        const soilGeometry = JSON.parse(soil.geometry);
        return {
          type: "Feature",
          geometry: soilGeometry,
          id: soil.gid,
          properties: { gid: soil.gid, musym: soil.musym },
        };
      });
      const soilFeatureCollection = {
        type: "FeatureCollection",
        features: soilFeatures,
      };
      setSoilData(soilFeatureCollection);

      if (fieldDetails.safeArea.rows.length > 0) {
        const safeFeatures = fieldDetails.safeArea.rows.map((safe) => {
          const safeGeometry = JSON.parse(safe.geometry);
          return { type: "Feature", geometry: safeGeometry };
        });
        const safeFeatureCollection = {
          type: "FeatureCollection",
          features: safeFeatures,
        };
        setSafeData(safeFeatureCollection);
      }

      if (fieldDetails.floodPlain.rows.length > 0) {
        const floodplainFeatures = fieldDetails.floodPlain.rows.map(
          (floodplain) => {
            const floodplainGeometry = JSON.parse(floodplain.geometry);
            return { type: "Feature", geometry: floodplainGeometry };
          }
        );
        const floodplainFeatureCollection = {
          type: "FeatureCollection",
          features: floodplainFeatures,
        };
        setFloodplainData(floodplainFeatureCollection);
      }

      if (fieldDetails.waterBuffer.rows.length > 0) {
        const waterBufferFeatures = fieldDetails.waterBuffer.rows.map(
          (waterBuffer) => {
            const waterBufferGeometry = JSON.parse(waterBuffer.geometry);
            return { type: "Feature", geometry: waterBufferGeometry };
          }
        );
        const waterBufferFeatureCollection = {
          type: "FeatureCollection",
          features: waterBufferFeatures,
        };
        setWaterData(waterBufferFeatureCollection);
      }

      if (fieldDetails.hydricSoil.rows.length > 0) {
        const hydricSoilFeatures = fieldDetails.hydricSoil.rows.map(
          (hydricSoil) => {
            const hydricSoilGeometry = JSON.parse(hydricSoil.geometry);
            return {
              type: "Feature",
              geometry: hydricSoilGeometry,
              id: hydricSoil.gid,
              properties: {
                gridcode: hydricSoil.gridcode,
                area: hydricSoil.area,
              },
            };
          }
        );
        const hydricSoilFeatureCollection = {
          type: "FeatureCollection",
          features: hydricSoilFeatures,
        };
        setHydroData(hydricSoilFeatureCollection);
      }

      if (fieldDetails.grasslandRentalRate.rows.length > 0) {
        const grasslandRecord = fieldDetails.grasslandRentalRate.rows[0];
        setGrasslandData({
          fips: grasslandRecord.fips,
          grr: grasslandRecord.grr,
        });
      }

      if (fieldDetails.pastureRentalRate.rows.length > 0) {
        const pastureRecord = fieldDetails.pastureRentalRate.rows[0];
        setPastureData({
          fips: pastureRecord.fips,
          mplrr: pastureRecord.mplrr,
        });
      }

      secureLocalStorage.setItem("field-details", fieldDetails);
    }
  }, [fieldDetails]);

  useEffect(() => {
    const rateBySoilTypes = majorSoilTypes.map((soilType) => {
      const areaInAcre = soilType.totalArea * 0.0002471;
      const rateBySoilType = soilType.srr * areaInAcre;
      return rateBySoilType;
    });
    const getTotalRate = (total, rate) => {
      return total + rate;
    };
    const totalRentalRate = Math.round(rateBySoilTypes.reduce(getTotalRate, 0));
    setFieldRentalRate(totalRentalRate);
  }, [majorSoilTypes]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !fieldFeatureData) return;

    const [minLng, minLat, maxLng, maxLat] = bbox(fieldFeatureData);
    mapRef.current.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 40, duration: 1000 }
    );
  }, [fieldFeatureData, mapLoaded]);

  useEffect(() => {
    if (preferredPractice === "CP88") {
      setRentalRateType("grassland");
    } else if (
      (preferredPractice === "CP22" &&
        landCoverSelections.length === 1 &&
        landCoverSelections[0] === "pasture") ||
      preferredPractice === "CP29" ||
      preferredPractice === "CP30"
    ) {
      setRentalRateType("pasture");
    } else {
      setRentalRateType("soil");
    }
  }, [preferredPractice]);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 700;

  useEffect(() => {
    if (
      userFavoritesLoaded &&
      !practiceLoading &&
      scoredAndSortedPractices.length > 0
    ) {
      // Delay a tick to let UI catch up
      setTimeout(() => setPracticesReady(true), 50);
    }
  }, [userFavoritesLoaded, practiceLoading, scoredAndSortedPractices]);

  useEffect(() => {
    const userId = secureLocalStorage.getItem("userRef");
    if (!userId) {
      setUserFavoritesLoaded(true); // not logged in — skip
      return;
    }

    if (selectedState && selectedCounty && !userFavoritesLoaded) {
      getUserFavorites();
    }
  }, [selectedState, selectedCounty]);

  useEffect(() => {
    return () => {
      const map = mapRef.current?.getMap?.();
      if (map?.setTerrain) map.setTerrain(null);
    };
  }, []);

  if (!fieldDetails) {
    navigate("/");
    return;
  }

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const formatCrepValue = (obj) => {
    if (!obj) return null;

    const percent = toNum(obj.percentage);
    if (percent !== null && percent > 0)
      return { type: "percent", value: percent };

    const perAcre = toNum(obj.perAcre);
    if (perAcre !== null && perAcre > 0)
      return { type: "perAcre", value: perAcre };

    return null;
  };

  // Returns the effective incentives to display for S/R/P/W
  const getEffectiveIncentives = (base, selectedCrep) => {
    const out = { ...base };

    if (!selectedCrep) return out;

    // SIP affects S
    const sip = formatCrepValue(selectedCrep.sip);
    if (sip) out.S = sip;
    else if (selectedCrep.sip && !sip) out.S = null;

    // PIP affects P
    const pip = formatCrepValue(selectedCrep.pip);
    if (pip) out.P = pip;
    else if (selectedCrep.pip && !pip) out.P = null;

    // Rental rate bonus from CREP (R) is additive to base R percent
    const baseR = toNum(base.R) || 0;
    const crepR = toNum(selectedCrep.newenrollmentsrr) || 0;
    const totalR = baseR + crepR;

    out.R = crepR > 0 ? { type: "percent", value: crepR } : null;

    out.W = null;

    return out;
  };

  const renderIncentives = (effective) => {
    const keys = ["S", "R", "P", "W"];

    return keys
      .map((key) => {
        const info = INCENTIVE_LABELS[key];
        if (!info) return null;

        const val = effective[key];
        if (!val) return null;

        // Allow old base shape (number) or new shape ({type,value})
        let display;
        if (typeof val === "number") {
          display = <b>{val}%</b>;
        } else if (val.type === "percent") {
          display = <b>{val.value}%</b>;
        } else if (val.type === "perAcre") {
          display = <b>${val.value.toLocaleString()}/acre</b>;
        } else {
          return null;
        }

        return (
          <div
            key={key}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "6px",
              marginBottom: "4px",
            }}
          >
            <span style={{ flexShrink: 0 }}>{ICONS[key]}</span>
            <span>
              {display} {info.label} <i>{info.note}</i>
            </span>
          </div>
        );
      })
      .filter(Boolean);
  };

  return (
    <>
      <Header />
      {contextHolder}
      <Spin spinning={loading}>
        <div className="report">
          <div className="report-head-container">
            <div
              className="report-basic-cont"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <h2
                className={printList ? "print report-header" : "report-header"}
              >
                CRP Field Eligibility Report:
                <span
                  style={{ fontWeight: "normal" }}
                >{` ${fieldBasics?.county}, ${fieldBasics?.state}`}</span>
              </h2>
              <div className="basic-info-container">
                <div className="report-basic-information">
                  <p></p>
                  <p>
                    <b>FIPS Code: </b>
                    {fieldBasics?.fips} | <b>Field Size: ≈ </b>
                    {Math.floor(fieldSize)} acres |{" "}
                    {/* <b>County Average Rental Rate*: </b>$
                    {Math.round(fieldBasics?.srr2024 * 100) / 100}/acre */}
                  </p>
                  <p>
                    <b>SAFE Zone(s): </b>
                    {fieldDetails.safeArea.rows.length === 0
                      ? "No"
                      : fieldDetails.safeArea.rows
                          .map((item) => safeLabels[item.code])
                          .join(", ")}{" "}
                    | <b>Floodplain: </b>
                    {fieldDetails.floodPlain.rows.length ? "Yes" : "No"} |{" "}
                    <b>Selected Priorities: </b>
                    {prioritySelections.length
                      ? prioritySelections.map((priority, index) => {
                          if (index === 0) {
                            return priorityLabels[priority];
                          } else {
                            return ", " + priorityLabels[priority];
                          }
                        })
                      : "No priority selected"}
                  </p>
                  <div className="report-btns">
                    <Button
                      className="report-button filter-button"
                      onClick={() => {
                        if (wizardStep < 1) dispatch(setWizardStep(1));
                        dispatch(openWizard());
                      }}
                    >
                      <IoFilter style={{ marginRight: 5 }} />
                      Update Filters
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="report-head-btn-container outer">
              <div className="report-head-btn-container top">
                {authToken === null ? (
                  <Button
                    className="report-button log-in"
                    onClick={() =>
                      navigate("/UserLogin", { state: { from: location } })
                    }
                  >
                    <LuLogIn className="report-button-icon" />
                    {!isMobile && <>Log In To Save</>}
                  </Button>
                ) : (
                  <Button className="report-button" onClick={showPopconfirm}>
                    <LuSave className="report-button-icon" />
                    {!isMobile && <>Save Field Report</>}
                  </Button>
                )}

                <ResponsiveConfirm
                  title="Clear Filters"
                  description="Are you sure you want to clear your selected field and filters and start over?"
                  confirmText="Yes"
                  cancelText="No"
                  onConfirm={() => resetFilterState(dispatch)}
                >
                  <Button className="report-button">
                    <b>
                      <LuListRestart className="report-button-icon" />{" "}
                      {!isMobile && <>Start Over</>}
                    </b>
                  </Button>
                </ResponsiveConfirm>
                <Button className="report-button print" onClick={handlePrint}>
                  <LuPrinter className="report-button-icon" />
                  {!isMobile && <>Print</>}
                </Button>

                <Button
                  className="report-button shapefile"
                  onClick={handleDownload}
                >
                  <FaFileDownload className="report-button-icon" />
                  {!isMobile && <>Download Shapefile</>}
                </Button>
              </div>
              <div className="report-head-btn-container bottom">
                <div className="select-signup-container-field-report">
                  <label style={{ fontWeight: "bolder" }}>Signup Types </label>
                  <Select
                    mode="multiple"
                    className="select-font-size sel-sgnup"
                    defaultValue={selectedSignUps}
                    placeholder="Select Signup"
                    onChange={handleSignupChange}
                    options={signupsList}
                    value={selectedSignUps}
                    showSearch={false}
                  />
                </div>
              </div>
            </div>
          </div>
          <hr className="report-hr" />
          <div className="map-practice-container">
            <div className="report-practice-container">
              {isMobile && (
                <>
                  <div className="report-map-container">
                    <h3
                      className={
                        printList
                          ? "print report-section-header"
                          : "report-section-header"
                      }
                    >
                      Field Boundary:
                    </h3>
                    <div className="report-map-inner-container">
                      <MapGL
                        id="fieldReportMap"
                        ref={mapRef}
                        mapboxAccessToken="pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                        preserveDrawingBuffer={true}
                        terrain={null}
                        initialViewState={{
                          longitude: -91,
                          latitude: 36,
                          zoom: 4,
                        }}
                        mapStyle={baseLayer}
                        interactiveLayerIds={interactiveLayerIds}
                        onMouseMove={onMouseMove}
                        onLoad={() => setMapLoaded(true)}
                      >
                        <Tooltip placement="bottom" title="Layer Tool">
                          <Button
                            className="report-layer-select"
                            onClick={handleLayerSelect}
                          >
                            <LuMap strokeWidth={3} />
                          </Button>
                        </Tooltip>
                        {layerSelectOpen && (
                          <div className="report-layer-options">
                            <div className="base-layers">
                              <p>
                                <b>Base Layers</b>
                              </p>
                              <p>
                                <img
                                  alt="thumbnail for light view of map"
                                  className="layer-thumbnail"
                                  src="https://api.mapbox.com/styles/v1/mapbox/light-v11/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                                />
                                <Switch
                                  className="report-layer-switch"
                                  checked={
                                    baseLayer ===
                                    "mapbox://styles/mapbox/satellite-streets-v12"
                                  }
                                  checkedChildren="Satellite"
                                  unCheckedChildren="light"
                                  defaultChecked
                                  onChange={(checked) => {
                                    const map = mapRef.current?.getMap?.();
                                    try {
                                      // ensure no terrain is active before style swap
                                      if (
                                        map?.getTerrain?.() &&
                                        map?.setTerrain
                                      )
                                        map.setTerrain(null);
                                    } catch {}
                                    setBaseLayer(
                                      checked
                                        ? "mapbox://styles/mapbox/satellite-streets-v12"
                                        : "mapbox://styles/mapbox/light-v11"
                                    );
                                  }}
                                />

                                <img
                                  alt="thumbnail for light view of map"
                                  className="layer-thumbnail"
                                  src="https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                                />
                              </p>
                            </div>
                            <hr />
                            <div className="supporting-layers">
                              <p>
                                <b>Supporting Layers</b>
                              </p>
                              <p>
                                <input
                                  type="checkbox"
                                  checked={soilLayerOn}
                                  onChange={(e) => {
                                    if (e.currentTarget.checked) {
                                      setSoilLayerOn(true);
                                    } else {
                                      setSoilLayerOn(false);
                                    }
                                  }}
                                />
                                Soil Types
                                <div className="report-legend-soil"></div>
                              </p>
                              <p>
                                <input
                                  type="checkbox"
                                  checked={safeLayerOn}
                                  onChange={(e) => {
                                    if (e.currentTarget.checked) {
                                      setSafeLayerOn(true);
                                    } else {
                                      setSafeLayerOn(false);
                                    }
                                  }}
                                />
                                SAFE Boundaries
                                <div className="report-legend-safe"></div>
                              </p>
                              <p>
                                <input
                                  type="checkbox"
                                  checked={floodplainLayerOn}
                                  onChange={(e) => {
                                    if (e.currentTarget.checked) {
                                      setFloodplainLayerOn(true);
                                    } else {
                                      setFloodplainLayerOn(false);
                                    }
                                  }}
                                />
                                Floodplains
                                <div className="report-legend-floodplain"></div>
                              </p>
                              <p>
                                <input
                                  type="checkbox"
                                  checked={waterLayerOn}
                                  onChange={(e) => {
                                    if (e.currentTarget.checked) {
                                      setWaterLayerOn(true);
                                    } else {
                                      setWaterLayerOn(false);
                                    }
                                  }}
                                />
                                Water Features
                                <div className="report-legend-water"></div>
                              </p>
                            </div>
                          </div>
                        )}
                        <Source
                          id="map-source"
                          type="vector"
                          url="mapbox://chuck0520.5pq9omti"
                          maxzoom={22}
                          minzoom={0}
                        >
                          <Layer
                            id="county-boundary"
                            source-layer="c_08mr23-1pp4eg"
                            type="line"
                            paint={{
                              "line-color": "#800000",
                              "line-width": 1,
                              "line-opacity": 0.4,
                            }}
                            minzoom={0}
                            maxzoom={22}
                            filter={stateFilter}
                          />
                        </Source>

                        <Source
                          id="field-source"
                          type="geojson"
                          data={fieldFeatureData || EMPTY_FC}
                        >
                          <Layer
                            id="field"
                            type="line"
                            paint={{ "line-color": "white", "line-width": 2 }}
                          />
                        </Source>

                        <Source
                          id="soil-source"
                          type="geojson"
                          data={soilData || EMPTY_FC}
                        >
                          <Layer
                            id="soil-layer"
                            type="fill"
                            paint={{
                              "fill-outline-color": "white",
                              "fill-color": "transparent",
                            }}
                            layout={{
                              visibility: soilLayerOn ? "visible" : "none",
                            }}
                          />
                          <Layer
                            id="soil-layer-highlight"
                            type="fill"
                            paint={{
                              "fill-color": "purple",
                              "fill-opacity": 0.5,
                            }}
                            filter={filter}
                            layout={{
                              visibility: soilLayerOn ? "visible" : "none",
                            }}
                          />
                        </Source>

                        <Source
                          id="safe-source"
                          type="geojson"
                          data={safeData || EMPTY_FC}
                        >
                          <Layer
                            id="safe"
                            type="fill"
                            paint={{
                              "fill-color": "orange",
                              "fill-opacity": 0.5,
                            }}
                            layout={{
                              visibility: safeLayerOn ? "visible" : "none",
                            }}
                          />
                        </Source>

                        <Source
                          id="floodplain-source"
                          type="geojson"
                          data={floodplainData || EMPTY_FC}
                        >
                          <Layer
                            id="floodplain"
                            type="fill"
                            paint={{
                              "fill-color": "blue",
                              "fill-opacity": 0.5,
                            }}
                            layout={{
                              visibility: floodplainLayerOn
                                ? "visible"
                                : "none",
                            }}
                          />
                        </Source>

                        <Source
                          id="water-source"
                          type="geojson"
                          data={waterData || EMPTY_FC}
                        >
                          <Layer
                            id="water"
                            type="fill"
                            paint={{
                              "fill-color": "cyan",
                              "fill-opacity": 0.5,
                            }}
                            layout={{
                              visibility: waterLayerOn ? "visible" : "none",
                            }}
                          />
                        </Source>

                        {fieldDetails.fieldRate.rows.map((soil) => {
                          const soilGeometry = JSON.parse(soil.geometry);
                          const [lng, lat] = getMarkerLocation(soilGeometry);
                          return (
                            <>
                              {soilLayerOn && (
                                <Marker
                                  key={soil.gid}
                                  longitude={lng}
                                  latitude={lat}
                                  anchor="center"
                                >
                                  <label style={{ color: "white" }}>
                                    {soil.musym}
                                  </label>
                                </Marker>
                              )}
                            </>
                          );
                        })}
                      </MapGL>
                    </div>
                  </div>
                  <div>
                    <p className="report-disclaimer">
                      Rental rates vary depending on cover type and practice
                      selection. Payment estimates from enrolling marginal
                      pastureland in CP22, CP29, and CP30 will be reflected
                      based on the county Marginal Pastureland Rental Rate
                      (MPLRR). Payment estimates from enrolling in CP88 will be
                      reflected based on the county Grassland Rental Rate (GRR).
                    </p>
                  </div>
                  <div className="report-rental-table-container">
                    <br />
                    <Tabs
                      defaultActiveKey="soil"
                      activeKey={rentalRateType}
                      onChange={(e) => setRentalRateType(e)}
                      type="card"
                      size="middle"
                      className="report-table-tabs"
                      items={[
                        {
                          label: "Soil Rate",
                          key: "soil",
                          children: (
                            <>
                              <h3
                                className={
                                  printList
                                    ? "report-section-header print"
                                    : "report-section-header"
                                }
                              >
                                Soil Rental Rate<sup>[1]</sup>:
                              </h3>

                              <p className="report-table-information">
                                This is a breakdown table of the soil types and
                                corresponding rental rate regarding your field.
                                Please find more about soil types in your county
                                from{" "}
                                <a
                                  href="https://www.nrcs.usda.gov/publications/Map%20Unit%20Description%20%28Brief%2C%20Generated%29%20Query%20by%20Soil%20Survey%20Area%20and%20Map%20Unit%20%28All%20Components%29.html"
                                  target="_blank"
                                  style={{ color: "maroon" }}
                                  rel="noreferrer"
                                >
                                  here
                                </a>
                                .
                              </p>
                              <Table
                                className={
                                  printList
                                    ? "print report-table"
                                    : "report-table"
                                }
                                columns={[
                                  {
                                    title: "Soil Type",
                                    dataIndex: "soil_type",
                                    key: "soil_type",
                                    align: "center",
                                  },
                                  {
                                    title: "Rental Rate",
                                    dataIndex: "rental_rate",
                                    key: "rental_rate",
                                    align: "center",
                                  },
                                  {
                                    title: "Acreage",
                                    dataIndex: "acreage",
                                    key: "acreage",
                                    align: "center",
                                  },
                                ]}
                                dataSource={soilTableData}
                                pagination={{
                                  pageSize: 3,
                                  position: ["bottomCenter"],
                                }}
                                rowClassName={(record) =>
                                  record.key === highlightFeatureId
                                    ? "report-table-highlight"
                                    : " "
                                }
                                size="large"
                              />
                            </>
                          ),
                        },
                        {
                          label: "Marginal Pastureland Rate",
                          key: "pasture",
                          children: (
                            <>
                              <h3
                                className={
                                  printList
                                    ? "report-section-header print"
                                    : "report-section-header"
                                }
                              >
                                Marginal Pastureland Rental Rate<sup>[1]</sup>:
                              </h3>
                              <p className="report-table-information">
                                This is the county rate for enrolling your field
                                in a Marginal Pastureland CRP practice (CP29,
                                CP30, and sometimes CP22).
                              </p>
                              <Table
                                className={
                                  printList
                                    ? "print report-table"
                                    : "report-table"
                                }
                                columns={[
                                  {
                                    title: "County",
                                    dataIndex: "county",
                                    key: "county",
                                    align: "center",
                                  },
                                  {
                                    title: "Rental Rate",
                                    dataIndex: "rental_rate",
                                    key: "rental_rate",
                                    align: "center",
                                  },
                                  {
                                    title: "Acreage",
                                    dataIndex: "acreage",
                                    key: "acreage",
                                    align: "center",
                                  },
                                ]}
                                dataSource={[
                                  {
                                    key: pastureData?.fips,
                                    county: fieldBasics?.county,
                                    rental_rate:
                                      "$" + pastureData?.mplrr + "/acre",
                                    acreage: Math.round(fieldSize * 100) / 100,
                                  },
                                ]}
                                pagination={false}
                                size="large"
                              />
                            </>
                          ),
                        },
                        {
                          label: "Grassland Rate",
                          key: "grassland",
                          children: (
                            <>
                              <h3
                                className={
                                  printList
                                    ? "report-section-header print"
                                    : "report-section-header"
                                }
                              >
                                Grassland Rental Rate<sup>[1]</sup>:
                              </h3>
                              <p className="report-table-information">
                                This is the county rate for enrolling your field
                                in a Grassland CRP practice (CP88).
                              </p>
                              <Table
                                className={
                                  printList
                                    ? "print report-table"
                                    : "report-table"
                                }
                                columns={[
                                  {
                                    title: "County",
                                    dataIndex: "county",
                                    key: "county",
                                    align: "center",
                                  },
                                  {
                                    title: "Rental Rate",
                                    dataIndex: "rental_rate",
                                    key: "rental_rate",
                                    align: "center",
                                  },
                                  {
                                    title: "Acreage",
                                    dataIndex: "acreage",
                                    key: "acreage",
                                    align: "center",
                                  },
                                ]}
                                dataSource={[
                                  {
                                    key: grasslandData?.fips,
                                    county: fieldBasics?.county,
                                    rental_rate:
                                      "$" + grasslandData?.grr + "/acre",
                                    acreage: Math.round(fieldSize * 100) / 100,
                                  },
                                ]}
                                pagination={false}
                                size="large"
                              />
                            </>
                          ),
                        },
                      ]}
                    />
                  </div>
                </>
              )}
              <h3
                className={
                  printList
                    ? "print report-section-header"
                    : "report-section-header"
                }
              >
                Eligible CRP Practices:
              </h3>

              <p style={{ width: "90%", marginTop: "-5px" }}>
                Please click on a practice below to view practice specific field
                rental rates and use the <em> Details </em> link for more
                information on each practice.
              </p>
              <Spin spinning={!practicesReady}>
                {practicesReady && (
                  <List
                    className="report-practice-list"
                    grid={{ gutter: 16, column: 1 }}
                    locale={{
                      emptyText: (
                        <Empty
                          description={
                            hydroFilteredPractices.length > 0 &&
                            selectedSignUps.length === 0
                              ? "Please select at least one type of signup to view practices"
                              : "No eligible practices found for your selections."
                          }
                        />
                      ),
                    }}
                    pagination={{
                      align: "center",
                      position: "bottom",
                      pageSize: pageSize,
                      current: pageIndex,
                      total: scoredAndSortedPractices?.length || 0,
                      onChange: (page) => setPageIndex(page),
                    }}
                    dataSource={scoredAndSortedPractices?.slice(
                      (pageIndex - 1) * pageSize,
                      pageIndex * pageSize
                    )}
                    renderItem={(practice, index) => {
                      const practiceTitle = practice.title
                        ?.split("-")[0]
                        .trim();

                      const isPastureRate =
                        (practiceTitle === "CP22" &&
                          landCoverSelections.length === 1 &&
                          landCoverSelections[0] === "pasture") ||
                        practiceTitle === "CP29" ||
                        practiceTitle === "CP30";

                      const isGrasslandRate = practiceTitle === "CP88";

                      const handlePracticeClick = () => {
                        if (preferredPractice !== practiceTitle) {
                          setPreferredPractice(practiceTitle);
                          setPreferredSignups({
                            G: !!practice.general,
                            C: !!practice.continuous,
                          });
                          const updatedIncentives = { S: 0, R: 0, P: 0, W: 0 };

                          practice.programs.forEach((p) => {
                            const { incentives } = p;
                            if (!incentives) return;
                            const sip = Number(incentives.sip);
                            if (!isNaN(sip))
                              updatedIncentives.S = Math.max(
                                updatedIncentives.S,
                                sip
                              );
                            const rri = Number(incentives.rentalRateIncentive);
                            if (!isNaN(rri))
                              updatedIncentives.R = Math.max(
                                updatedIncentives.R,
                                rri
                              );
                            const pip = Number(incentives.pip);
                            if (!isNaN(pip))
                              updatedIncentives.P = Math.max(
                                updatedIncentives.P,
                                pip
                              );
                            const wq = Number(
                              incentives.waterqualitythruclear30
                            );
                            if (!isNaN(wq))
                              updatedIncentives.W = Math.max(
                                updatedIncentives.W,
                                wq
                              );
                          });

                          setPreferredIncentives(updatedIncentives);
                        } else {
                          setPreferredPractice(null);
                        }
                        if (isTutorialActive && currentStep === 56)
                          handleNextStep();
                        setCrepOption("NO CREP");
                      };

                      const getEstimatedPayment = (paymentMultiplier) => {
                        const sipMultiplier = preferredIncentives.S
                          ? 1 + preferredIncentives.S / 100
                          : 1;
                        const rentalRateMultiplier = preferredIncentives.R
                          ? 1 + preferredIncentives.R / 100
                          : 1;
                        const pipMultiplier = preferredIncentives.P
                          ? 1 + preferredIncentives.P / 100
                          : 1;

                        const totalFieldPayment = Math.round(
                          fieldRentalRate *
                            sipMultiplier *
                            rentalRateMultiplier *
                            pipMultiplier *
                            paymentMultiplier
                        );

                        const maximumFieldPayment =
                          paymentMultiplier === 0.9
                            ? Math.round(300 * fieldSize)
                            : paymentMultiplier === 0.85
                            ? Math.round(240 * fieldSize)
                            : totalFieldPayment;

                        const estimatedFieldPayment =
                          totalFieldPayment <= maximumFieldPayment
                            ? totalFieldPayment
                            : maximumFieldPayment;

                        return estimatedFieldPayment;
                      };

                      const getPasturePayment = (paymentMultiplier) => {
                        const sipMultiplier = preferredIncentives.S
                          ? 1 + preferredIncentives.S / 100
                          : 1;
                        const rentalRateMultiplier = preferredIncentives.R
                          ? 1 + preferredIncentives.R / 100
                          : 1;
                        const pipMultiplier = preferredIncentives.P
                          ? 1 + preferredIncentives.P / 100
                          : 1;

                        const totalPasturePayment = Math.round(
                          pastureData?.mplrr *
                            fieldSize *
                            sipMultiplier *
                            rentalRateMultiplier *
                            pipMultiplier *
                            paymentMultiplier
                        );

                        const maximumFieldPayment =
                          paymentMultiplier === 0.9
                            ? Math.round(300 * fieldSize)
                            : paymentMultiplier === 0.85
                            ? Math.round(240 * fieldSize)
                            : totalPasturePayment;

                        const estimatedPasturePayment =
                          totalPasturePayment <= maximumFieldPayment
                            ? totalPasturePayment
                            : maximumFieldPayment;

                        return estimatedPasturePayment;
                      };

                      practice.crepInfo?.forEach((crep) => {
                        const title = crep.crepname;
                        if (!hasCrepColor(title)) {
                          setCrepColor(
                            title,
                            crepColorPool[nextColorIndex % crepColorPool.length]
                          );
                          nextColorIndex++;
                        }
                      });

                      // Same pasture logic you used in the old branch
                      const isPasturePractice = (practiceTitle) =>
                        (practiceTitle === "CP22" &&
                          landCoverSelections.length === 1 &&
                          landCoverSelections[0] === "pasture") ||
                        practiceTitle === "CP29" ||
                        practiceTitle === "CP30";

                      // Grassland signup (CP88) special case
                      const isGrasslandPractice = (practiceTitle) =>
                        practiceTitle === "CP88";

                      const getPerAcreBaseRateInfo = (practiceTitle) => {
                        if (isPasturePractice(practiceTitle)) {
                          return {
                            label:
                              "Your marginal pastureland rental rate per acre",
                            // pastureData.mplrr is already dollars/acre
                            perAcre: pastureData?.mplrr
                              ? Math.round(pastureData.mplrr)
                              : null,
                          };
                        }

                        if (isGrasslandPractice(practiceTitle)) {
                          return {
                            label: "Your grassland rental rate per acre",
                            // grasslandData.grr is dollars/acre
                            perAcre: grasslandData?.grr
                              ? Math.round(grasslandData.grr)
                              : null,
                          };
                        }

                        // Default soil-based field average
                        const perAcre =
                          majorSoilArea && fieldRentalRate
                            ? Math.round(fieldRentalRate / majorSoilArea)
                            : null;

                        return {
                          label:
                            "Your total weighted average base rental rate per acre",
                          perAcre,
                        };
                      };

                      const renderSignupPercentSentence = () => {
                        const { G, C } = preferredSignups || {};

                        if (G && C) {
                          return (
                            <>
                              {" "}
                              You will receive 85% for general signup and 90%
                              for continuous signup of the estimated base rental
                              rate.
                            </>
                          );
                        }
                        if (G) {
                          return (
                            <>
                              {" "}
                              You will receive 85% for general signup of the
                              estimated base rental rate.
                            </>
                          );
                        }
                        if (C) {
                          return (
                            <>
                              {" "}
                              You will receive 90% for continuous signup of the
                              estimated base rental rate.
                            </>
                          );
                        }
                        return null;
                      };

                      const renderSignupCapSentence = (practiceNumber) => {
                        const { G, C } = preferredSignups || {};

                        if (G && C) {
                          return (
                            <>
                              <b>{practiceNumber}</b> is eligible for both
                              general and continuous signup. General signup has
                              a cap of $240/acre and continuous signup has a cap
                              of $300/acre.
                            </>
                          );
                        }

                        if (G) {
                          return (
                            <>
                              <b>{practiceNumber}</b> is eligible for general
                              signup which has a cap of $240/acre.
                            </>
                          );
                        }

                        if (C) {
                          return (
                            <>
                              <b>{practiceNumber}</b> is eligible for continuous
                              signup which has a cap of $300/acre.
                            </>
                          );
                        }

                        return null;
                      };

                      const renderCrepStateIncentiveSentence = (
                        option,
                        practiceNumber
                      ) => {
                        const stateIncentive = Number(option.stateincentive);
                        if (
                          !stateIncentive ||
                          isNaN(stateIncentive) ||
                          stateIncentive <= 0
                        ) {
                          return null; // no special state incentive to show
                        }

                        return (
                          <>
                            <b>{practiceNumber}</b> enrolled via the{" "}
                            <b>{option.crepname}</b> initiative may also receive
                            an additonal annual{" "}
                            <b>${stateIncentive.toLocaleString()}/acre</b> state
                            incentive.
                          </>
                        );
                      };

                      const { label, perAcre } = getPerAcreBaseRateInfo(
                        practice.practiceNumber
                      );

                      const selectedCrepOption =
                        crepOption === "NO CREP"
                          ? null
                          : practice.crepInfo?.find(
                              (c) => c.crepname === crepOption
                            ) || null;

                      const effectiveIncentives = getEffectiveIncentives(
                        preferredIncentives,
                        selectedCrepOption
                      );

                      return (
                        <List.Item key={index}>
                          <div className="report-practice-detail-container">
                            <button
                              onClick={handlePracticeClick}
                              className="practice-card-container"
                            >
                              <PracticeCard
                                mode="check_eligibility"
                                disableInteractions={true}
                                practice={practice}
                                listView="Compact"
                                selectable={true}
                                favOnchange={favOnchange}
                                selected={preferredPractice === practiceTitle}
                                preferredPractice={preferredPractice}
                                setPreferredPractice={setPreferredPractice}
                                setPreferredSignups={setPreferredSignups}
                                setPreferredIncentives={setPreferredIncentives}
                                userFavoritePractices={userFavoritePractices}
                                crepColorMap={crepColors}
                                isMostCommon={practice.isMostCommon}
                              />
                            </button>
                            <div
                              className="report-practice-detail"
                              style={{
                                ...(preferredPractice !== practiceTitle && {
                                  borderBottom: "none",
                                  paddingBottom: "0",
                                }),
                              }}
                            >
                              {preferredPractice === practiceTitle && (
                                <>
                                  {practice.crepInfo && (
                                    <Radio.Group
                                      className="report-crep-options"
                                      defaultValue="NO CREP"
                                      value={crepOption}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setCrepOption(value);
                                        if (
                                          isTutorialActive &&
                                          currentStep === 64 &&
                                          value !== "NO CREP"
                                        ) {
                                          handleNextStep();
                                        }
                                      }}
                                    >
                                      <Radio.Button
                                        className="report-crep-button"
                                        value="NO CREP"
                                        style={{
                                          color: "white",
                                          backgroundColor:
                                            crepOption === "NO CREP"
                                              ? "#808080"
                                              : "#80808066",
                                          border: "1px white solid",
                                          borderRadius: "10px 10px 0px 0px",
                                        }}
                                      >
                                        {preferredSignups.G && (
                                          <span>General </span>
                                        )}
                                        {preferredSignups.G &&
                                          preferredSignups.C && <span>&</span>}
                                        {preferredSignups.C && (
                                          <span> Continuous</span>
                                        )}
                                        {!(
                                          preferredSignups.G &&
                                          preferredSignups.C
                                        ) && <span> Signup</span>}
                                      </Radio.Button>

                                      {practice.crepInfo
                                        .filter((crep) =>
                                          fieldDetails.crepArea.rows
                                            .map((item) => item.code)
                                            .includes(crepCodes[crep.crepname])
                                        )
                                        .map((option) => {
                                          const baseColor =
                                            getCrepColor(option.crepname) ||
                                            "#888888";

                                          const isSelected =
                                            crepOption === option.crepname;

                                          return (
                                            <Radio.Button
                                              key={option.crepname}
                                              className="report-crep-button"
                                              value={option.crepname}
                                              style={{
                                                color: "white",
                                                backgroundColor: isSelected
                                                  ? baseColor
                                                  : `${baseColor}66`,
                                                border: "1px white solid",
                                                borderRadius:
                                                  "10px 10px 0px 0px",
                                              }}
                                            >
                                              {option.crepname}
                                            </Radio.Button>
                                          );
                                        })}
                                    </Radio.Group>
                                  )}

                                  <div
                                    className="report-practice-detail crep-payment"
                                    style={{
                                      backgroundColor: "whitesmoke",
                                      border: `2px solid ${
                                        selectedCrepOption
                                          ? getCrepColor(
                                              selectedCrepOption.crepname
                                            ) || "#888888"
                                          : "gray"
                                      }`,
                                    }}
                                  >
                                    <div className="report-dropdown-cont">
                                      <div className="report-dropdown-left">
                                        {isPasturePractice(practiceTitle) ? (
                                          <>
                                            <h2>
                                              Marginal Pastureland Rental Rate
                                            </h2>

                                            <Table
                                              className="dropdown-report-table"
                                              size="small"
                                              pagination={false}
                                              columns={[
                                                {
                                                  title: "County",
                                                  dataIndex: "county",
                                                  key: "county",
                                                  align: "center",
                                                },
                                                {
                                                  title: "Rate",
                                                  dataIndex: "rental_rate",
                                                  key: "rental_rate",
                                                  align: "center",
                                                },
                                                {
                                                  title: "Acreage",
                                                  dataIndex: "acreage",
                                                  key: "acreage",
                                                  align: "center",
                                                },
                                              ]}
                                              dataSource={[
                                                {
                                                  key: "pasture",
                                                  county: fieldBasics?.county,
                                                  rental_rate:
                                                    pastureData?.mplrr
                                                      ? `$${Math.round(
                                                          pastureData.mplrr
                                                        ).toLocaleString()}/acre`
                                                      : "N/A",
                                                  acreage: fieldSize
                                                    ? Math.round(
                                                        fieldSize * 100
                                                      ) / 100
                                                    : "N/A",
                                                },
                                              ]}
                                            />

                                            <p>
                                              {label} is estimated to be:{" "}
                                              {perAcre !== null ? (
                                                <>
                                                  <b>
                                                    ${perAcre.toLocaleString()}
                                                    /acre
                                                  </b>
                                                  <sup>[1]</sup>.
                                                </>
                                              ) : (
                                                "N/A."
                                              )}
                                              {renderSignupPercentSentence()}
                                            </p>
                                          </>
                                        ) : isGrasslandPractice(
                                            practiceTitle
                                          ) ? (
                                          <>
                                            <h2>Grassland Rental Rate</h2>

                                            <Table
                                              className="dropdown-report-table"
                                              pagination={false}
                                              size="small"
                                              columns={[
                                                {
                                                  title: "County",
                                                  dataIndex: "county",
                                                  key: "county",
                                                  align: "center",
                                                },
                                                {
                                                  title: "Rate",
                                                  dataIndex: "rental_rate",
                                                  key: "rental_rate",
                                                  align: "center",
                                                },
                                                {
                                                  title: "Acreage",
                                                  dataIndex: "acreage",
                                                  key: "acreage",
                                                  align: "center",
                                                },
                                              ]}
                                              dataSource={[
                                                {
                                                  key: "grassland",
                                                  county: fieldBasics?.county,
                                                  rental_rate:
                                                    grasslandData?.grr
                                                      ? `$${Math.round(
                                                          grasslandData.grr
                                                        ).toLocaleString()}/acre`
                                                      : "N/A",
                                                  acreage: fieldSize
                                                    ? Math.round(
                                                        fieldSize * 100
                                                      ) / 100
                                                    : "N/A",
                                                },
                                              ]}
                                            />

                                            <p>
                                              {label} is estimated to be:{" "}
                                              {perAcre !== null ? (
                                                <>
                                                  <b>
                                                    ${perAcre.toLocaleString()}
                                                    /acre
                                                  </b>
                                                  <sup>[1]</sup>.
                                                </>
                                              ) : (
                                                "N/A."
                                              )}
                                              {renderSignupPercentSentence()}
                                            </p>
                                          </>
                                        ) : (
                                          <>
                                            <h2>Predominant Soil Types</h2>

                                            <Table
                                              className="dropdown-report-table"
                                              pagination={false}
                                              size="small"
                                              columns={[
                                                {
                                                  title: "Type",
                                                  dataIndex: "soil_type",
                                                  key: "soil_type",
                                                  align: "center",
                                                },
                                                {
                                                  title: "Rate",
                                                  dataIndex: "rental_rate",
                                                  key: "rental_rate",
                                                  align: "center",
                                                },
                                                {
                                                  title: "Acreage",
                                                  dataIndex: "acreage",
                                                  key: "acreage",
                                                  align: "center",
                                                },
                                              ]}
                                              dataSource={topThreeSoilsTable}
                                            />

                                            <p>
                                              {hasMissingTopThreeSrr ? (
                                                <>
                                                  Your field includes a soil
                                                  type which is outside of our
                                                  data set. Please contact your
                                                  local USDA office for more
                                                  information.
                                                </>
                                              ) : (
                                                <>
                                                  {label} is estimated to be:{" "}
                                                  {perAcre !== null ? (
                                                    <>
                                                      <b>
                                                        $
                                                        {perAcre.toLocaleString()}
                                                        /acre
                                                      </b>
                                                      <sup>[1]</sup>.
                                                    </>
                                                  ) : (
                                                    "N/A."
                                                  )}
                                                  {renderSignupPercentSentence()}
                                                </>
                                              )}
                                            </p>
                                          </>
                                        )}
                                      </div>

                                      <div className="report-dropdown-right">
                                        <h2>Possible Incentives</h2>
                                        <p>
                                          By signing up for{" "}
                                          <b>{practice.practiceNumber}</b>, you
                                          may be eligible for incentives that
                                          would increase your payment.
                                        </p>

                                        {/* Pass the selected CREP option so R can show “+ XX% CREP incentive” */}
                                        {renderIncentives(effectiveIncentives)}

                                        {/* Cap sentence when NOT using CREP */}
                                        {crepOption === "NO CREP" ? (
                                          <p>
                                            {renderSignupCapSentence(
                                              practice.practiceNumber
                                            )}
                                          </p>
                                        ) : (
                                          (() => {
                                            const stateIncentive = toNum(
                                              selectedCrepOption?.stateincentive
                                            );
                                            if (
                                              !stateIncentive ||
                                              stateIncentive <= 0
                                            )
                                              return null;
                                          })()
                                        )}

                                        {/* CREP-specific state incentive when CREP is selected and applicable */}
                                        {crepOption !== "NO CREP" &&
                                          selectedCrepOption && (
                                            <p>
                                              {renderCrepStateIncentiveSentence(
                                                selectedCrepOption,
                                                practice.practiceNumber
                                              )}
                                            </p>
                                          )}

                                        <p>
                                          <i>
                                            *Payment calculations will use the
                                            actual acres enrolled, not the
                                            entire field’s acreage.
                                          </i>
                                        </p>
                                        {/* Details link (matches selected tab: General/Continuous vs CREP) */}
                                        <div className="detail-right field-report">
                                          <p className="detail-button">
                                            <a
                                              href={
                                                crepOption !== "NO CREP" &&
                                                selectedCrepOption?.crepname
                                                  ? `/Practice/${selectedFips}/${btoa(
                                                      practice.pdfLink
                                                    )}/${btoa(
                                                      practice.title
                                                    )}/${btoa(
                                                      selectedCrepOption.crepname
                                                    )}`
                                                  : `/Practice/${selectedFips}/${btoa(
                                                      practice.pdfLink
                                                    )}/${btoa(practice.title)}`
                                              }
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                color: "maroon",
                                                fontWeight: "bold",
                                              }}
                                            >
                                              Details <FaAngleDoubleRight />
                                            </a>
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* {crepOption === "NO CREP" &&
                                    getPayEstimateText(
                                      getEstimatedPayment,
                                      getPasturePayment,
                                      practice,
                                      practice.practiceNumber
                                    )}

                                  {practice.crepInfo &&
                                    practice.crepInfo
                                      .filter((crep) =>
                                        fieldDetails.crepArea.rows
                                          .map((item) => item.code)
                                          .includes(crepCodes[crep.crepname])
                                      )
                                      .map(
                                        (option) =>
                                          crepOption === option.crepname &&
                                          getPayEstimateTextWithCREP(
                                            option,
                                            getEstimatedPayment,
                                            getPasturePayment,
                                            practice,
                                            practice.practiceNumber
                                          )
                                      )} */}
                                </>
                              )}
                            </div>
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                )}
              </Spin>
              {isMobile && (
                <div className="local-usda-container">
                  <h3
                    className={
                      printList
                        ? "report-section-header print"
                        : "report-section-header"
                    }
                  >
                    Local USDA Office:
                  </h3>
                  <div
                    className={
                      printList
                        ? "report-usda-information print"
                        : "report-usda-information"
                    }
                  >
                    <p>
                      Please reach out to your local USDA Farm Service Agency
                      office for further information.
                    </p>
                    <Table
                      className={
                        printList ? "print report-table" : "report-table"
                      }
                      columns={[
                        {
                          title: "Service Center",
                          dataIndex: "service_center",
                          key: "service_center",
                          align: "center",
                        },
                        {
                          title: "Address",
                          dataIndex: "physical_address",
                          key: "physical_address",
                          align: "center",
                        },
                        {
                          title: "Phone",
                          dataIndex: "phone",
                          key: "phone",
                          align: "center",
                        },
                      ]}
                      dataSource={serviceCenterList}
                      pagination={false}
                      size="large"
                    />
                  </div>
                </div>
              )}
            </div>
            {!isMobile && (
              <div className="map-rental-container">
                <div className="report-map-container">
                  <h3
                    className={
                      printList
                        ? "print report-section-header"
                        : "report-section-header"
                    }
                  >
                    Field Boundary:
                  </h3>
                  <div className="report-map-inner-container">
                    <MapGL
                      id="fieldReportMap"
                      ref={mapRef}
                      mapboxAccessToken="pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                      preserveDrawingBuffer={true}
                      terrain={null}
                      initialViewState={{
                        longitude: -91,
                        latitude: 36,
                        zoom: 4,
                      }}
                      mapStyle={baseLayer}
                      interactiveLayerIds={interactiveLayerIds}
                      onMouseMove={onMouseMove}
                      onLoad={() => setMapLoaded(true)}
                    >
                      <Tooltip placement="bottom" title="Layer Tool">
                        <Button
                          className="report-layer-select"
                          onClick={handleLayerSelect}
                        >
                          <LuMap strokeWidth={3} />
                        </Button>
                      </Tooltip>
                      {layerSelectOpen && (
                        <div className="report-layer-options">
                          <div className="base-layers">
                            <p>
                              <b>Base Layers</b>
                            </p>
                            <p>
                              <img
                                alt="thumbnail for light view of map"
                                className="layer-thumbnail"
                                src="https://api.mapbox.com/styles/v1/mapbox/light-v11/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                              />
                              <Switch
                                className="report-layer-switch"
                                checked={
                                  baseLayer ===
                                  "mapbox://styles/mapbox/satellite-streets-v12"
                                }
                                checkedChildren="Satellite"
                                unCheckedChildren="light"
                                defaultChecked
                                onChange={(checked) => {
                                  const map = mapRef.current?.getMap?.();
                                  try {
                                    // ensure no terrain is active before style swap
                                    if (map?.getTerrain?.() && map?.setTerrain)
                                      map.setTerrain(null);
                                  } catch {}
                                  setBaseLayer(
                                    checked
                                      ? "mapbox://styles/mapbox/satellite-streets-v12"
                                      : "mapbox://styles/mapbox/light-v11"
                                  );
                                }}
                              />

                              <img
                                alt="thumbnail for light view of map"
                                className="layer-thumbnail"
                                src="https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                              />
                            </p>
                          </div>
                          <hr />
                          <div className="supporting-layers">
                            <p>
                              <b>Supporting Layers</b>
                            </p>
                            <p>
                              <input
                                type="checkbox"
                                checked={soilLayerOn}
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setSoilLayerOn(true);
                                  } else {
                                    setSoilLayerOn(false);
                                  }
                                }}
                              />
                              Soil Types
                              <div className="report-legend-soil"></div>
                            </p>
                            <p>
                              <input
                                type="checkbox"
                                checked={safeLayerOn}
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setSafeLayerOn(true);
                                  } else {
                                    setSafeLayerOn(false);
                                  }
                                }}
                              />
                              SAFE Boundaries
                              <div className="report-legend-safe"></div>
                            </p>
                            <p>
                              <input
                                type="checkbox"
                                checked={floodplainLayerOn}
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setFloodplainLayerOn(true);
                                  } else {
                                    setFloodplainLayerOn(false);
                                  }
                                }}
                              />
                              Floodplains
                              <div className="report-legend-floodplain"></div>
                            </p>
                            <p>
                              <input
                                type="checkbox"
                                checked={waterLayerOn}
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setWaterLayerOn(true);
                                  } else {
                                    setWaterLayerOn(false);
                                  }
                                }}
                              />
                              Water Features
                              <div className="report-legend-water"></div>
                            </p>
                          </div>
                        </div>
                      )}
                      <Source
                        id="map-source"
                        type="vector"
                        url="mapbox://chuck0520.5pq9omti"
                        maxzoom={22}
                        minzoom={0}
                      >
                        <Layer
                          id="county-boundary"
                          source-layer="c_08mr23-1pp4eg"
                          type="line"
                          paint={{
                            "line-color": "#800000",
                            "line-width": 1,
                            "line-opacity": 0.4,
                          }}
                          minzoom={0}
                          maxzoom={22}
                          filter={stateFilter}
                        />
                      </Source>

                      <Source
                        id="field-source"
                        type="geojson"
                        data={fieldFeatureData || EMPTY_FC}
                      >
                        <Layer
                          id="field"
                          type="line"
                          paint={{ "line-color": "white", "line-width": 2 }}
                        />
                      </Source>

                      <Source
                        id="soil-source"
                        type="geojson"
                        data={soilData || EMPTY_FC}
                      >
                        <Layer
                          id="soil-layer"
                          type="fill"
                          paint={{
                            "fill-outline-color": "white",
                            "fill-color": "transparent",
                          }}
                          layout={{
                            visibility: soilLayerOn ? "visible" : "none",
                          }}
                        />
                        <Layer
                          id="soil-layer-highlight"
                          type="fill"
                          paint={{
                            "fill-color": "purple",
                            "fill-opacity": 0.5,
                          }}
                          filter={filter}
                          layout={{
                            visibility: soilLayerOn ? "visible" : "none",
                          }}
                        />
                      </Source>

                      <Source
                        id="safe-source"
                        type="geojson"
                        data={safeData || EMPTY_FC}
                      >
                        <Layer
                          id="safe"
                          type="fill"
                          paint={{
                            "fill-color": "orange",
                            "fill-opacity": 0.5,
                          }}
                          layout={{
                            visibility: safeLayerOn ? "visible" : "none",
                          }}
                        />
                      </Source>

                      <Source
                        id="floodplain-source"
                        type="geojson"
                        data={floodplainData || EMPTY_FC}
                      >
                        <Layer
                          id="floodplain"
                          type="fill"
                          paint={{ "fill-color": "blue", "fill-opacity": 0.5 }}
                          layout={{
                            visibility: floodplainLayerOn ? "visible" : "none",
                          }}
                        />
                      </Source>

                      <Source
                        id="water-source"
                        type="geojson"
                        data={waterData || EMPTY_FC}
                      >
                        <Layer
                          id="water"
                          type="fill"
                          paint={{ "fill-color": "cyan", "fill-opacity": 0.5 }}
                          layout={{
                            visibility: waterLayerOn ? "visible" : "none",
                          }}
                        />
                      </Source>

                      {fieldDetails.fieldRate.rows.map((soil) => {
                        const soilGeometry = JSON.parse(soil.geometry);
                        const [lng, lat] = getMarkerLocation(soilGeometry);
                        return (
                          <>
                            {soilLayerOn && (
                              <Marker
                                key={soil.gid}
                                longitude={lng}
                                latitude={lat}
                                anchor="center"
                              >
                                <label style={{ color: "white" }}>
                                  {soil.musym}
                                </label>
                              </Marker>
                            )}
                          </>
                        );
                      })}
                    </MapGL>
                  </div>
                </div>
                <div>
                  <p className="report-disclaimer">
                    Rental rates vary depending on cover type and practice
                    selection. Payment estimates from enrolling marginal
                    pastureland in CP22, CP29, and CP30 will be reflected based
                    on the county Marginal Pastureland Rental Rate (MPLRR).
                    Payment estimates from enrolling in CP88 will be reflected
                    based on the county Grassland Rental Rate (GRR).
                  </p>
                </div>
                <div className="report-rental-table-container">
                  <br />
                  <Tabs
                    defaultActiveKey="soil"
                    activeKey={rentalRateType}
                    onChange={(e) => setRentalRateType(e)}
                    type="card"
                    size="middle"
                    className="report-table-tabs"
                    items={[
                      {
                        label: "Soil Rate",
                        key: "soil",
                        children: (
                          <>
                            <h3
                              className={
                                printList
                                  ? "report-section-header print"
                                  : "report-section-header"
                              }
                            >
                              Soil Rental Rate<sup>[1]</sup>:
                            </h3>

                            <p className="report-table-information">
                              This is a breakdown table of the soil types and
                              corresponding rental rate regarding your field.
                              Please find more about soil types in your county
                              from{" "}
                              <a
                                href="https://www.nrcs.usda.gov/publications/Map%20Unit%20Description%20%28Brief%2C%20Generated%29%20Query%20by%20Soil%20Survey%20Area%20and%20Map%20Unit%20%28All%20Components%29.html"
                                target="_blank"
                                style={{ color: "maroon" }}
                                rel="noreferrer"
                              >
                                here
                              </a>
                              .
                            </p>
                            <Table
                              className={
                                printList
                                  ? "print report-table"
                                  : "report-table"
                              }
                              columns={[
                                {
                                  title: "Soil Type",
                                  dataIndex: "soil_type",
                                  key: "soil_type",
                                  align: "center",
                                },
                                {
                                  title: "Rental Rate",
                                  dataIndex: "rental_rate",
                                  key: "rental_rate",
                                  align: "center",
                                },
                                {
                                  title: "Acreage",
                                  dataIndex: "acreage",
                                  key: "acreage",
                                  align: "center",
                                },
                              ]}
                              dataSource={soilTableData}
                              pagination={{
                                pageSize: 3,
                                position: ["bottomCenter"],
                              }}
                              rowClassName={(record) =>
                                record.key === highlightFeatureId
                                  ? "report-table-highlight"
                                  : " "
                              }
                              size="large"
                            />
                          </>
                        ),
                      },
                      {
                        label: "Marginal Pastureland Rate",
                        key: "pasture",
                        children: (
                          <>
                            <h3
                              className={
                                printList
                                  ? "report-section-header print"
                                  : "report-section-header"
                              }
                            >
                              Marginal Pastureland Rental Rate<sup>[1]</sup>:
                            </h3>
                            <p className="report-table-information">
                              This is the county rate for enrolling your field
                              in a Marginal Pastureland CRP practice (CP29,
                              CP30, and sometimes CP22).
                            </p>
                            <Table
                              className={
                                printList
                                  ? "print report-table"
                                  : "report-table"
                              }
                              columns={[
                                {
                                  title: "County",
                                  dataIndex: "county",
                                  key: "county",
                                  align: "center",
                                },
                                {
                                  title: "Rental Rate",
                                  dataIndex: "rental_rate",
                                  key: "rental_rate",
                                  align: "center",
                                },
                                {
                                  title: "Acreage",
                                  dataIndex: "acreage",
                                  key: "acreage",
                                  align: "center",
                                },
                              ]}
                              dataSource={[
                                {
                                  key: pastureData?.fips,
                                  county: fieldBasics?.county,
                                  rental_rate:
                                    "$" + pastureData?.mplrr + "/acre",
                                  acreage: Math.round(fieldSize * 100) / 100,
                                },
                              ]}
                              pagination={false}
                              size="large"
                            />
                          </>
                        ),
                      },
                      {
                        label: "Grassland Rental Rate",
                        key: "grassland",
                        children: (
                          <>
                            <h3
                              className={
                                printList
                                  ? "report-section-header print"
                                  : "report-section-header"
                              }
                            >
                              Grassland Rental Rate<sup>[1]</sup>:
                            </h3>
                            <p className="report-table-information">
                              This is the county rate for enrolling your field
                              in a Grassland CRP practice (CP88).
                            </p>
                            <Table
                              className={
                                printList
                                  ? "print report-table"
                                  : "report-table"
                              }
                              columns={[
                                {
                                  title: "County",
                                  dataIndex: "county",
                                  key: "county",
                                  align: "center",
                                },
                                {
                                  title: "Rental Rate",
                                  dataIndex: "rental_rate",
                                  key: "rental_rate",
                                  align: "center",
                                },
                                {
                                  title: "Acreage",
                                  dataIndex: "acreage",
                                  key: "acreage",
                                  align: "center",
                                },
                              ]}
                              dataSource={[
                                {
                                  key: grasslandData?.fips,
                                  county: fieldBasics?.county,
                                  rental_rate:
                                    "$" + grasslandData?.grr + "/acre",
                                  acreage: Math.round(fieldSize * 100) / 100,
                                },
                              ]}
                              pagination={false}
                              size="large"
                            />
                          </>
                        ),
                      },
                    ]}
                  />
                </div>
                <div className="local-usda-container">
                  <h3
                    className={
                      printList
                        ? "report-section-header print"
                        : "report-section-header"
                    }
                  >
                    Local USDA Office:
                  </h3>
                  <div
                    className={
                      printList
                        ? "report-usda-information print"
                        : "report-usda-information"
                    }
                  >
                    <p>
                      Please reach out to your local USDA Farm Service Agency
                      office for further information.
                    </p>
                    <Table
                      className={
                        printList ? "print report-table" : "report-table"
                      }
                      columns={[
                        {
                          title: "Service Center",
                          dataIndex: "service_center",
                          key: "service_center",
                          align: "center",
                        },
                        {
                          title: "Address",
                          dataIndex: "physical_address",
                          key: "physical_address",
                          align: "center",
                        },
                        {
                          title: "Phone",
                          dataIndex: "phone",
                          key: "phone",
                          align: "center",
                        },
                      ]}
                      dataSource={serviceCenterList}
                      pagination={false}
                      size="large"
                    />
                  </div>
                </div>
                <div>
                  <h3
                    className={
                      printList
                        ? "report-section-header print"
                        : "report-section-header"
                    }
                  >
                    Disclaimer:
                  </h3>
                  <p>
                    [1] Please note that the estimated rental rate is a rough
                    estimation based on the CRP practice you selected. It may
                    vary due to your regional USDA official's final decision.
                    All dollar amounts presented are approximations derived from
                    soil type and rental rate data available at the time of this
                    tool's publication.
                  </p>
                </div>
              </div>
            )}
          </div>
          <hr className="report-hr" />

          <div className="report-general-info-container">
            <div>
              <h3
                className={
                  printList
                    ? "report-section-header print"
                    : "report-section-header"
                }
              >
                Acknowledgement:
              </h3>
              <div
                className={
                  printList ? "report-disclaimer print" : "report-disclaimer"
                }
              >
                <p>
                  This material is based upon work supported by the U.S.
                  Department of Agriculture, Farm Production and Conservation
                  program under agreement number FBC22CPT0012776. Any opinions,
                  findings, conclusions, or recommendations expressed in this
                  publication are those of the author(s) and do not necessarily
                  reflect the views of the U.S. Department of Agriculture. In
                  addition, any reference to specific brands or types of
                  products or services does not constitute or imply an
                  endorsement by the U.S. Department of Agriculture for those
                  products or services. USDA is an equal opportunity provider,
                  employer, and lender
                  (https://www.usda.gov/non-discrimination-statement). This work
                  is produced with support from the Mississippi State University
                  Forest and Wildlife Research Center and Mississippi State
                  University Geosystems Research Institute.
                </p>
              </div>
            </div>
          </div>
          <Modal
            centered={true}
            title="Save My Search"
            open={isSavePopOpen}
            onOk={null}
            footer={null}
            onCancel={saveSearchPopClose}
          >
            <Form
              name="basic"
              labelCol={{
                span: 8,
              }}
              form={form}
              wrapperCol={{
                span: 16,
              }}
              initialValues={{
                remember: true,
              }}
              onFinish={storeSearchData}
              // onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <Form.Item
                label="Save Search"
                name="SaveKey"
                rules={[
                  {
                    required: true,
                    message: "Please input your Save Search!",
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                wrapperCol={{
                  offset: 8,
                  span: 16,
                }}
              >
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </Form.Item>
            </Form>
          </Modal>
          <Modal
            centered={true}
            className="filter-modal"
            open={isWizardOpen}
            width="80vw"
            onOk={() => {
              dispatch(closeWizard());
            }}
            onCancel={() => {
              dispatch(closeWizard());
            }}
            cancelButtonProps={{ style: { display: "none" } }}
            okButtonProps={{ style: { display: "none" } }}
            footer={[
              wizardStep > 0 && (
                <Button
                  key="back"
                  style={{ margin: "0 8px" }}
                  onClick={() => dispatch(prevWizardStep())}
                >
                  Back
                </Button>
              ),
              wizardStep < 3 && (
                <Button
                  key="next"
                  className="location-sel-next"
                  style={{ float: "right" }}
                  type="primary"
                  onClick={() => dispatch(nextWizardStep())}
                >
                  Next
                </Button>
              ),
              wizardStep === 3 && (
                <Button
                  key="confirm"
                  className="confirm-tutorial"
                  style={{ float: "right" }}
                  type="primary"
                  onClick={() => dispatch(closeWizard())}
                >
                  Confirm
                </Button>
              ),
            ]}
          >
            <WizardForm
              className="filter-wizard-form"
              // selectedPriorities={onPrioritiesSelect}
              // selectedCheckboxes={selectedCheckboxes}
              // setRankingScore={onSetRankingScore}
              // notselected={notselected}
            />
          </Modal>
        </div>
      </Spin>
      <Footer />
    </>
  );
};

export default FieldReport;
